import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Pencil } from "lucide-react";

import UserDetailSection from "../../../client_details/component/User_Details_Section/UserDetailSection";
import PageHeader from "../../../Ui_Components/PageHeader";
import CreateDishModal, {
  DishItem,
  EditingOptionData,
} from "./components/CreateDishModal";
import ViewMealOptionModal from "./components/ViewMealOptionModal";
import DuplicateMealOptionModal from "./components/DuplicateMealOptionModal";
import MealPlanPreviewModal from "./components/MealPlanPreviewModal";
import MealPlanDuplicateAndTimingsPopup from "../MealPlanDuplicateAndTimingsPopup";
import EditNutrientValuesModal, {
  EditNutrientValuesPayload,
} from "./components/EditNutrientValuesModal";
import { DragPlaceholder } from "./components/Drag_and_Drop/DragPlaceholder";

import { BASE_URL, FIREBASE_COLLECTIONS } from "../../../api/url";
import {
  getMealPlanDetail,
  deleteMealCardPlan,
  getGlobalTemplateDetails,
  getDuplicateMealOptionDetails,
} from "../../../../services/mealPlans";
import {
  rejectSuggestion,
  approveSuggestion,
  publishGlobalTemplate,
  getSuggestedPlanGlobalTemplateDetails,
  deleteGlobalTemplateMealOptions,
  duplicateGlobalTemplateMealOptions,
  updateTemplateName,
} from "../../../../services/globalTemplates";
import {
  getAdhocMealPlanDetails,
  deleteAdhocMealPlanOption,
  renameAdhocMealPlan,
  duplicateAdhocMealPlanOption,
  publishAdhocMealPlan,
  downloadAdhocMealPlan,
  editAdhocMealPlanNutrientValues,
} from "../../../../services/adhocMealPlans";
import { useAuth } from "../../../../context/auth/AuthContext";
import { db } from "../../../../config/firebase";
import { doc, getDoc } from "firebase/firestore";
import "../../../styles/useCustomScrollbar";
import { ToastContainer, useToast } from "../../../Ui_Components/ToastMessage";
import { Button } from "../../../Ui_Components/Button";
import { useMealOptionDragDrop } from "./components/Drag_and_Drop/useMealOptionDragDrop";
import { calculateCPFRatio, formatCPFRatio } from "../CPFCalculator";
import useCustomScrollbar from "../../../styles/useCustomScrollbar";
import { usePermissions } from "../../../../hooks/usePermissions";

interface MealPlanDetailsPageProps {}

interface MealPlan {
  id: number;
  plan_name: string;
  meal_type: string;
  created_by: string;
  created_on: string;
  cal: number;
  adj_prot: number;
  carb: number;
  fat: number;
  gl: number;
  status: "Active" | "Approval Pending" | "Draft" | "In-Active" | "Archived";
}

interface MealTiming {
  id: string;
  mealType: string;
  time: { hour: string; minute: string; period: string };
  fk_user_meal: number;
}

interface MealOption {
  id: string;
  name: string;
  mealTimingId: string;
  dishes: DishItem[];
  instructions?: string;
  totals: {
    cal: number;
    carb: number;
    fat: number;
    protein: number;
    gl: number;
  };
  isNewlyCreated?: boolean;
  optionNumber?: number;
}

interface NutritionData {
  total_cals_max: number;
  total_cals_min: number;
  cals_units: string;
  fat_max: number;
  fat_min: number;
  fat_units: string;
  carbs_max: number;
  carbs_min: number;
  carbs_units: string;
  protein_max: number;
  protein_min: number;
  protein_units: string;
  GL_max: number;
  GL_min: number;
  GL_units: string;
  CPF_max: string;
  CPF_min: string;
  current_cal: number;
  current_protein: number;
  current_carbs: number;
  current_fats: number;
  current_gl: number;
  current_CPF: string;
}

interface ApiMealPlanData {
  user_meal_type: string;
  user_meal_type_id: number;
  fk_user_meal: number;
  meal_time?: string; // original field — e.g. "06:00:00"
  // ─── NEW API fields (added) — new API sends these instead of meal_time ────
  hour?: number; // e.g. 5
  minute?: number; // e.g. 0
  period?: string; // e.g. "AM"
  // ─────────────────────────────────────────────────────────────────────────
  Options: any[];
  meal_avg_calories: string;
  meal_avg_protein: string;
  meal_avg_carbs: string;
  meal_avg_fats: string;
  meal_avg_gl: string;
  meal_avg_cpf: string;
}

interface DuplicatingProgress {
  mealTimingId: string;
  progress: number;
  newName: string;
  sourceOption: MealOption;
}

// Updated shimmerStyles constant - Better visual feedback for drop zones

const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(to right, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%);
    background-size: 1000px 100%;
  }
  
  @keyframes glow-fade {
  0%, 20% {
    border-color: #ABEBEB;
    box-shadow: 0 0 6px 2px rgba(171, 235, 235, 0.4),
                0 0 10px 3px rgba(171, 235, 235, 0.3),
                0 0 12px 4px rgba(171, 235, 235, 0.2);
  }
  100% { 
    border-color: #E5E5EA;
    box-shadow: 0 0 0 0 rgba(171, 235, 235, 0);
  }
}

.new-card-glow {
  animation: glow-fade 4s ease-out forwards;
  position: relative;
  z-index: 10;
}

  
  /* Drag and Drop Styles - UPDATED for card-based dragging */
  .dragging-item {
    opacity: 0.4 !important;
    cursor: grabbing !important;
  }
  
  /* Make the entire card draggable (except when view-only) */
  [data-option-id]:not(.view-only) {
    cursor: grab;
    user-select: none;
    /* Ensure card receives pointer events */
    pointer-events: auto;
  }
  
  [data-option-id]:not(.view-only):active {
    cursor: grabbing;
  }
  
  /* Smooth transitions for cards */
  .meal-options-container > div > [data-option-id] {
    transition: transform 0.2s ease, opacity 0.2s ease;
  }
  
  /* Add visual feedback when dragging */
  [data-option-id].dragging-item {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
  
  /* Prevent text selection during drag */
  .meal-options-container.is-dragging * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
  
  /* Better drop zone visibility */
  .bg-\\[\\#E5F8F8\\] {
    z-index: 100;
    position: relative;
    pointer-events: all !important;
  }
  
  /* Ensure placeholders are above cards when showing */
  .meal-options-container > div > .bg-\\[\\#E5F8F8\\] {
    z-index: 100 !important;
  }
  
  /* CRITICAL: Allow cards to receive drag events during dragging */
  .meal-options-container.is-dragging [data-option-id]:not(.dragging-item) {
    /* Allow pointer events for drag detection */
    pointer-events: auto !important;
  }
  
  /* But disable clicking on interactive elements */
  .meal-options-container.is-dragging [data-option-id]:not(.dragging-item) button,
  .meal-options-container.is-dragging [data-option-id]:not(.dragging-item) a {
    pointer-events: none;
  }
  
  /* Ensure drag events work on the card itself */
  .meal-options-container.is-dragging [data-option-id]:not(.dragging-item) {
    position: relative;
  }
  
  /* Debug: Show when hovering (optional - remove in production) */
  .meal-options-container.is-dragging [data-option-id]:not(.dragging-item):hover {
    outline: 2px solid rgba(0, 126, 127, 0.2);
    outline-offset: -2px;
  }
`;
// ─── Nutrition Stats ──────────────────────────────────────────────────────────

const NutritionStatsDisplay: React.FC<{
  nutritionData: NutritionData;
  loading?: boolean;
  onEditClick?: () => void;
}> = ({ nutritionData, loading = false, onEditClick }) => {
  if (loading) {
    return (
      <div className="mx-6 mb-4">
        <div className="bg-white px-4 py-4 border-[0.5px] border-[#F3F4F6] rounded-md">
          <div className="grid grid-cols-6 gap-6 w-full animate-pulse">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i}>
                <div className="h-4 bg-gray-200 rounded mb-2" />
                <div className="h-[3px] bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const pct = (current: number, max: number) =>
    max === 0 ? 0 : Math.min(100, (current / max) * 100);

  return (
    <div className="mx-6 mb-4">
      <div className="bg-white px-4 py-4 border-[0.5px] border-[#F3F4F6] rounded-md overflow-x-auto custom-scrollbar-overlay pb-2">
        <div className="grid grid-cols-[repeat(6,1fr)_auto] gap-6 w-full items-end">
          {[
            {
              label: "Cal",
              unit: nutritionData.cals_units,
              current: nutritionData.current_cal,
              max: nutritionData.total_cals_max,
              color: "#B44AE6",
            },
            {
              label: "(A)Prot.",
              unit: nutritionData.protein_units,
              current: nutritionData.current_protein,
              max: nutritionData.protein_max,
              color: "#6BBDFA",
            },
            {
              label: "Carb",
              unit: nutritionData.carbs_units,
              current: nutritionData.current_carbs,
              max: nutritionData.carbs_max,
              color: "#FFB532",
            },
            {
              label: "Fat",
              unit: nutritionData.fat_units,
              current: nutritionData.current_fats,
              max: nutritionData.fat_max,
              color: "#F25BEC",
            },
            {
              label: "GL",
              unit: nutritionData.GL_units,
              current: nutritionData.current_gl,
              max: nutritionData.GL_max,
              color: "#A0CA07",
            },
          ].map(({ label, unit, current, max, color }) => (
            <div key={label}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#1F1F1F] text-[12px] font-medium font-inter">
                  {label}
                  {label !== "GL" && <span className="text-[#8D8D8D]"> ({unit})</span>}
                </span>
                <span className="text-[#1F1F1F] text-[12px] font-medium font-inter">
                  {current.toFixed(0)}/{max.toFixed(0)}
                </span>
              </div>
              <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full"
                  style={{ width: `${pct(current, max)}%`, background: color }}
                />
              </div>
            </div>
          ))}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1F1F1F] text-[12px] font-medium font-inter">
                C:P:F
              </span>
              <span className="text-[#1F1F1F] text-[12px] font-medium font-inter">
                {nutritionData.current_CPF} / {nutritionData.CPF_max}
              </span>
            </div>
            {/* <div className="w-full h-[3px] bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-[#007AFF]" style={{ width: "0%" }} />
            </div> */}
          </div>
          {onEditClick && (
            <button
              type="button"
              onClick={onEditClick}
              className="text-[#8E8E93] hover:text-[#0DB5B5] transition-colors p-1 self-center"
              aria-label="Edit nutrient values"
              title="Edit nutrient values"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Duplicating Progress Card ────────────────────────────────────────────────

const DuplicatingProgressCard: React.FC<{ name: string; progress: number }> = ({
  name,
  progress,
}) => (
  <div
    className="bg-white border-[0.5px] border-[#E5E5EA] rounded-md mb-2 flex items-center justify-center"
    style={{ height: "140px" }}
  >
    <div className="w-full max-w-[200px] px-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[#646464]">Duplicating...</span>
        <span className="text-xs font-medium text-[#1F1F1F]">{progress}%</span>
      </div>
      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#0DB5B5] transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  </div>
);

// ─── Meal Card ────────────────────────────────────────────────────────────────

const MealCard: React.FC<{
  option: MealOption;
  isViewOnly: boolean;
  onClick: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  cardProps?: any; // NEW: replaces dragHandleProps and dropZoneProps
  dragClassName?: string;
}> = ({
  option,
  isViewOnly,
  onClick,
  onEdit,
  onDuplicate,
  cardProps, // NEW
  dragClassName = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const optionsDropdownRef = useRef<HTMLDivElement>(null);
  const dishCount = option.dishes.filter((d) => d.name).length;

  useEffect(() => {
    if (isExpanded) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        optionsDropdownRef.current &&
        !optionsDropdownRef.current.contains(event.target as Node)
      ) {
        setShowOptionsDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Prevent drag when clicking on interactive elements
  const handleInteractiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className={`bg-white border-[0.5px] border-[#E5E5EA] rounded-md p-3 mb-2 transition-all ${
        option.isNewlyCreated ? "new-card-glow" : ""
      } ${dragClassName} ${!isViewOnly ? "cursor-grab active:cursor-grabbing" : ""}`}
      data-option-id={option.id}
      {...(cardProps || {})} // NEW: Apply all drag/drop handlers to the entire card
    >
      <div className="flex justify-between items-start mb-1 gap-2">
        <h5
          onClick={(e) => {
            handleInteractiveClick(e);
            onClick();
          }}
          className="text-[#1F1F1F] text-xs 3xl:text-sm font-medium font-inter flex-1 cursor-pointer hover:text-[#0DB5B5] transition-colors"
        >
          {option.name}
        </h5>

        {/* 3-dot menu — hidden when plan is Active */}
        {!isViewOnly && (
          <div
            className="relative flex-shrink-0"
            ref={optionsDropdownRef}
            onClick={handleInteractiveClick}
          >
            <button
              onClick={(e) => {
                handleInteractiveClick(e);
                setShowOptionsDropdown(!showOptionsDropdown);
              }}
              className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="4" r="1" fill="currentColor" />
                <circle cx="8" cy="8" r="1" fill="currentColor" />
                <circle cx="8" cy="12" r="1" fill="currentColor" />
              </svg>
            </button>
            {showOptionsDropdown && (
              <div
                className="absolute right-0 top-full mt-1 bg-white border border-[#E5E5EA] rounded-md shadow-lg min-w-[100px] z-50"
                onClick={handleInteractiveClick}
              >
                <button
                  onClick={(e) => {
                    handleInteractiveClick(e);
                    setShowOptionsDropdown(false);
                    onEdit();
                  }}
                  className="w-full px-2 py-1.5 text-left text-sm text-[#1F1F1F] hover:bg-[#E5F8F8] cursor-pointer transition-colors flex items-center"
                >
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    handleInteractiveClick(e);
                    setShowOptionsDropdown(false);
                    onDuplicate();
                  }}
                  className="w-full px-2 py-1.5 text-left text-sm text-[#1F1F1F] hover:bg-[#E5F8F8] cursor-pointer transition-colors flex items-center"
                >
                  Duplicate
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border-b-[0.5px] border-[#E9E9E9] mt-1 -m-3 mb-2" />

      <div
        onClick={(e) => {
          handleInteractiveClick(e);
          onClick();
        }}
        className="space-y-1 mb-2 h-[60px] overflow-y-auto custom-scrollbar-overlay -mr-2  cursor-pointer"
      >
        {option.dishes
          .filter((d) => d.name)
          .map((dish) => (
            <div
              key={dish.id}
              className="flex items-center text-[10px] 3xl:text-[12px] text-[#202020] font-inter group"
            >
              <span
                className="w-[60%] truncate mr-[10%]"
                title={dish.name.trim()}
              >
                {dish.name.trim()}
              </span>
              <span
                className="w-[30%] truncate text-right whitespace-nowrap"
                title={`${dish.quantity} ${dish.unit.trim()}`}
              >
                {dish.quantity}{" "}
                <span className="text-[#AEAEB2]">{dish.unit.trim()}</span>
              </span>
            </div>
          ))}
      </div>

      <div className="border-t-[0.5px] border-[#E9E9E9] mt-2 -m-3 bg-[#F9FEFE] rounded-b-md overflow-hidden">
        <button
          onClick={(e) => {
            handleInteractiveClick(e);
            setIsExpanded(!isExpanded);
          }}
          className="w-full py-1.5 px-3 flex items-center justify-between"
        >
          <span className="text-[10px] 3xl:text-[12px] text-[#007E7F] font-medium">
            Nutrients
          </span>
          <span className="text-[10px] text-[#1F1F1F] font-medium">
            {dishCount.toString().padStart(2, "0")}
          </span>
        </button>

        {(isExpanded || shouldRender) && (
          <div
            className={`transition-all duration-300 ease-out overflow-hidden ${isExpanded ? "max-h-24 opacity-100" : "max-h-0 opacity-0"}`}
          >
            <div className="px-3 pb-2 pt-1">
              <div className="flex items-center justify-between text-[10px] mb-1.5">
                <div className="flex items-center gap-1">
                  <span className="text-[#646464]">Cal -</span>
                  <span className="font-medium text-[#B44AE6]">
                    {option.totals.cal.toFixed(0)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[#646464]">Carb -</span>
                  <span className="font-medium text-[#FFB532]">
                    {option.totals.carb.toFixed(0)}g
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[#646464]">Fats -</span>
                  <span className="font-medium text-[#F25BEC]">
                    {option.totals.fat.toFixed(0)}g
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <div className="flex items-center gap-1">
                  <span className="text-[#646464]">A.pro -</span>
                  <span className="font-medium text-[#6BBDFA]">
                    {option.totals.protein.toFixed(0)}g
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[#646464]">GL -</span>
                  <span className="font-medium text-[#A0CA07]">
                    {option.totals.gl.toFixed(0)}
                  </span>
                </div>
                <div className="flex items-center gap-1 opacity-0">
                  <span>Spacer</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Meal Timing Card ─────────────────────────────────────────────────────────

const MealTimingCard: React.FC<{
  meal: MealTiming;
  isExpanded: boolean;
  isViewOnly: boolean;
  onCreateClick: () => void;
  mealOptions: MealOption[];
  onOptionClick: (option: MealOption) => void;
  onOptionEdit: (option: MealOption) => void;
  onOptionDuplicate: (option: MealOption) => void;
  duplicatingProgress: DuplicatingProgress | null;
  getCardProps?: (itemId: string, mealTimingId: string) => any;
  getItemClassName?: (itemId: string, mealTimingId: string) => string;
  getMealTimingDropZoneProps?: (mealTimingId: string) => any;
  shouldShowPlaceholder?: (
    mealTimingId: string,
    position: "before" | "after" | "empty",
    itemId?: string | null,
  ) => boolean;
  onDropAtPosition?: (
    mealTimingId: string,
    beforeItemId: string | null,
    position: "top" | "bottom" | null,
  ) => void;
}> = ({
  meal,
  isExpanded,
  isViewOnly,
  onCreateClick,
  mealOptions,
  onOptionClick,
  onOptionEdit,
  onOptionDuplicate,
  duplicatingProgress,
  getCardProps,
  getItemClassName,
  getMealTimingDropZoneProps,
  shouldShowPlaceholder,
  onDropAtPosition,
}) => {
  const formatTime = (h: string, m: string, p: string) => `${h}:${m} ${p}`;
  const optionsForThisMeal = mealOptions.filter(
    (opt) => opt.mealTimingId === meal.id,
  );
  const isDuplicatingInThisMeal = duplicatingProgress?.mealTimingId === meal.id;
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkScrollbar = () => {
      if (containerRef.current) {
        const hasScroll =
          containerRef.current.scrollHeight > containerRef.current.clientHeight;
        setHasScrollbar(hasScroll);
      }
    };

    checkScrollbar();
    window.addEventListener("resize", checkScrollbar);

    const observer = new MutationObserver(checkScrollbar);
    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener("resize", checkScrollbar);
      observer.disconnect();
    };
  }, [optionsForThisMeal.length, duplicatingProgress]);

  return (
    <div className="h-full">
      <div className="bg-white border-[0.5px] border-[#E5E5EA] rounded-md h-full flex flex-col meal-timing-card-container">
        <div className="flex-shrink-0 flex justify-between items-start  px-4 pt-4 ">
          <div>
            <h4 className="text-[#1F1F1F] text-sm 3xl:text-base font-medium">
              {meal.mealType}
            </h4>
            <p className="text-[#8E8E93] text-[10px] 3xl:text-[12px]">
              {formatTime(meal.time.hour, meal.time.minute, meal.time.period)}
            </p>
          </div>
          <span className="text-[#1F1F1F] font-medium text-[12px] 3xl:text-sm px-[6px] py-[1px] rounded-[6px] bg-[#EFEFEF]">
            {optionsForThisMeal.length}
          </span>
        </div>

        <div
          ref={containerRef}
          className="flex-1 min-h-0 px-4 pt-2 overflow-y-auto custom-scrollbar-overlay meal-options-container"
          {...(getMealTimingDropZoneProps
            ? getMealTimingDropZoneProps(meal.id)
            : {})}
        >
          <div className={`flex flex-col ${hasScrollbar ? "-mr-[8px] " : ""}`}>
            {optionsForThisMeal.length === 0 ? (
              <>
                {shouldShowPlaceholder &&
                  shouldShowPlaceholder(meal.id, "empty") && (
                    <DragPlaceholder
                      height="140px"
                      dropZoneProps={
                        getMealTimingDropZoneProps
                          ? getMealTimingDropZoneProps(meal.id)
                          : undefined
                      }
                    />
                  )}
              </>
            ) : (
              <div className="flex flex-col gap-3 py-3 -my-3">
                {/* Add top padding for glow effect visibility */}
                <div className="h-1" />
                {optionsForThisMeal.map((option, index) => (
                  <React.Fragment key={option.id}>
                    {/* Placeholder BEFORE this card (top half) */}
                    {shouldShowPlaceholder &&
                      shouldShowPlaceholder(meal.id, "before", option.id) && (
                        <DragPlaceholder
                          dropZoneProps={{
                            onDragEnter: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                            onDragOver: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.dataTransfer.dropEffect = "move";
                            },
                            onDragLeave: (e) => {
                              e.stopPropagation();
                            },
                            onDrop: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Drop BEFORE this card (at top position)
                              if (onDropAtPosition) {
                                onDropAtPosition(meal.id, option.id, "top");
                              }
                            },
                          }}
                        />
                      )}

                    {/* The actual card */}
                    <MealCard
                      option={option}
                      isViewOnly={isViewOnly}
                      onClick={() => onOptionClick(option)}
                      onEdit={() => onOptionEdit(option)}
                      onDuplicate={() => onOptionDuplicate(option)}
                      cardProps={
                        getCardProps
                          ? getCardProps(option.id, meal.id)
                          : undefined
                      }
                      dragClassName={
                        getItemClassName
                          ? getItemClassName(option.id, meal.id)
                          : ""
                      }
                    />

                    {/* Placeholder AFTER this card (bottom half) */}
                    {shouldShowPlaceholder &&
                      shouldShowPlaceholder(meal.id, "after", option.id) && (
                        <DragPlaceholder
                          dropZoneProps={{
                            onDragEnter: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            },
                            onDragOver: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              e.dataTransfer.dropEffect = "move";
                            },
                            onDragLeave: (e) => {
                              e.stopPropagation();
                            },
                            onDrop: (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              // Drop AFTER this card (at bottom position)
                              if (onDropAtPosition) {
                                onDropAtPosition(meal.id, option.id, "bottom");
                              }
                            },
                          }}
                        />
                      )}

                    {/* Progress bar for duplication */}
                    {isDuplicatingInThisMeal &&
                      duplicatingProgress &&
                      duplicatingProgress.sourceOption.id === option.id && (
                        <DuplicatingProgressCard
                          name={duplicatingProgress.newName}
                          progress={duplicatingProgress.progress}
                        />
                      )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          {!isViewOnly && (
            <button
              onClick={onCreateClick}
              className="mt-3 flex items-center gap-1.5 text-[#0DB5B5] text-sm 3xl:text-base font-medium hover:bg-[#F1FBFB] rounded-[6px] w-full px-2 py-1.5 transition-colors mb-3"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 3.5V12.5M3.5 8H12.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              Create
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

function calcLocalCurrentNutrition(
  mealTimings: Array<{ id: string }>,
  mealOptions: Array<{
    mealTimingId: string;
    totals: {
      cal: number;
      carb: number;
      fat: number;
      protein: number;
      gl: number;
    };
  }>,
  formatFn?: (
    carb: number,
    protein: number,
    fat: number,
    cal: number,
  ) => string,
): {
  current_cal: number;
  current_carbs: number;
  current_fats: number;
  current_protein: number;
  current_gl: number;
  current_CPF: string;
} {
  const grand = { cal: 0, carb: 0, fat: 0, protein: 0, gl: 0 };

  mealTimings.forEach((timing) => {
    const opts = mealOptions.filter((o) => o.mealTimingId === timing.id);
    if (opts.length === 0) return;

    const sum = opts.reduce(
      (acc, opt) => ({
        cal: acc.cal + opt.totals.cal,
        carb: acc.carb + opt.totals.carb,
        fat: acc.fat + opt.totals.fat,
        protein: acc.protein + opt.totals.protein,
        gl: acc.gl + opt.totals.gl,
      }),
      { cal: 0, carb: 0, fat: 0, protein: 0, gl: 0 },
    );

    // Average for this timing, then add to grand total
    grand.cal += sum.cal / opts.length;
    grand.carb += sum.carb / opts.length;
    grand.fat += sum.fat / opts.length;
    grand.protein += sum.protein / opts.length;
    grand.gl += sum.gl / opts.length;
  });

  // CPF — reuse the project's CPFCalculator if available, else derive inline
  let current_CPF = "0:0:0";
  if (formatFn) {
    current_CPF = formatFn(grand.carb, grand.protein, grand.fat, grand.cal);
  } else {
    // Inline fallback (matches the MealPlanPreviewModal calcCPF helper)
    const calFromMacros = grand.carb * 4 + grand.protein * 4 + grand.fat * 9;
    if (calFromMacros > 0) {
      const c = Math.round(((grand.carb * 4) / calFromMacros) * 100);
      const p = Math.round(((grand.protein * 4) / calFromMacros) * 100);
      const f = Math.max(0, 100 - c - p);
      current_CPF = `${c}:${p}:${f}`;
    }
  }

  return {
    current_cal: grand.cal,
    current_carbs: grand.carb,
    current_fats: grand.fat,
    current_protein: grand.protein,
    current_gl: grand.gl,
    current_CPF,
  };
}

// ─── Scroll Minimap ───────────────────────────────────────────────────────────

const ScrollPositionIndicator: React.FC<{
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  totalItems: number;
}> = ({ scrollContainerRef, totalItems }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(20);
  const [isDragging, setIsDragging] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const minimapRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = scrollWidth - clientWidth;

      // Check if horizontal scrolling is needed
      setIsScrollable(scrollWidth > clientWidth);

      setScrollPosition(maxScroll > 0 ? scrollLeft / maxScroll : 0);
      setViewportWidth(
        Math.min(100, Math.max(5, (clientWidth / scrollWidth) * 100)),
      );
    };
    update();
    container.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      container.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [scrollContainerRef, totalItems]);

  const handleInteraction = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current;
    const inner = innerRef.current;
    if (!container || !inner) return;
    const { left, width } = inner.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - left) / width));
    const { scrollWidth, clientWidth } = container;
    const maxScroll = scrollWidth - clientWidth;
    const scrollable = 1 - clientWidth / scrollWidth;
    container.scrollLeft =
      scrollable > 0 ? (Math.min(scrollable, pct) / scrollable) * maxScroll : 0;
  };

  // Only show if there are items AND horizontal scrolling is needed
  if (totalItems === 0 || !isScrollable) return null;

  const maxPos = 100 - viewportWidth;
  const pos = Math.min(scrollPosition * 100, maxPos);

  return (
    <div className="fixed bottom-4 right-6 z-10">
      <div
        ref={minimapRef}
        className="relative w-[136px] h-[60px] bg-white border-[0.5px] border-[#E5E5EA] rounded cursor-pointer p-1.5 shadow-md"
        onMouseDown={(e) => {
          setIsDragging(true);
          handleInteraction(e);
        }}
        onMouseMove={(e) => {
          if (isDragging) handleInteraction(e);
        }}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
      >
        <div ref={innerRef} className="absolute inset-1.5">
          <div className="flex gap-0.5 h-full">
            {Array.from({ length: totalItems }).map((_, i) => (
              <div key={i} className="flex-1 bg-[#F5F5F5] rounded-sm" />
            ))}
          </div>
          <div
            className="absolute top-0 h-full border-2 border-[#0DB5B5] rounded-sm bg-transparent transition-all duration-200 ease-out pointer-events-none"
            style={{ left: `${pos}%`, width: `${viewportWidth}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const MealPlanDetailsPage: React.FC<MealPlanDetailsPageProps> = () => {
  useCustomScrollbar();
  const { userId, planId } = useParams<{ userId: string; planId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

// Deactivated clients are fully read-only regardless of plan status
const isDeactivated =
  (location.state as { isDeactivated?: boolean } | null)?.isDeactivated === true;

// Approval mode: reviewer is checking a suggested template submitted by another nutritionist
const approvalState = location.state as {
  approvalMode?: boolean;
  fromRejected?: boolean;
  fromGlobal?: boolean;
  globalTemplateMode?: boolean;
  adhocMealMode?: boolean;
  isNewPlan?: boolean;
  templateStatus?: string;
  mealPlan?: MealPlan;
  mealTimings?: Array<{ id: string; mealType: string; time: { hour: string; minute: string; period: string } }>;
  submitterInfo?: { name: string; role: string; goal: string; avatarInitial: string; avatarColor: string; photo?: string };
  rejectionInfo?: { reason: string; reviewedByName: string; reviewedOn: string };
} | null;
const isApprovalMode = approvalState?.approvalMode === true;
const isRejectedSuggestionMode = approvalState?.fromRejected === true;
const isGlobalTemplateMode =
  approvalState?.globalTemplateMode === true ||
  location.pathname.startsWith("/global-template/plan/");
const isSuggestionMode = location.pathname.startsWith("/global-template/suggestion/");
const isAdhocMealMode =
  approvalState?.adhocMealMode === true ||
  location.pathname.startsWith("/adhoc-meal/");
const submitterInfo = approvalState?.submitterInfo;
const rejectionInfo = approvalState?.rejectionInfo;
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [mealPlan, setMealPlan] = useState<MealPlan | null>(null);
  const [isEditingPlanName, setIsEditingPlanName] = useState(false);
  const [editingPlanNameValue, setEditingPlanNameValue] = useState("");
  const [isSavingPlanName, setIsSavingPlanName] = useState(false);
  const planNameInputRef = useRef<HTMLInputElement>(null);
  const [apiRejectionMeta, setApiRejectionMeta] = useState<{ reason: string; reviewedByName: string; reviewedByRole: string } | null>(null);
  const [apiSubmitterPhoto, setApiSubmitterPhoto] = useState<string>("");
  const { user } = useAuth();
  const [generatedTemplateId, setGeneratedTemplateId] = useState<number | null>(null);
  const [generatedTemplateCode, setGeneratedTemplateCode] = useState<string>("");
  const [mealTimings, setMealTimings] = useState<MealTiming[]>([]);
  const [mealOptions, setMealOptions] = useState<MealOption[]>([]);
  const [isUserDetailExpanded, setIsUserDetailExpanded] = useState(false);
  const [nutritionData, setNutritionData] = useState<NutritionData | null>(
    null,
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState("");
  const [selectedMealTimingId, setSelectedMealTimingId] = useState("");
  const [selectedUserMealId, setSelectedUserMealId] = useState<number>(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingOptionData, setEditingOptionData] = useState<
    EditingOptionData | undefined
  >(undefined);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedViewOption, setSelectedViewOption] =
    useState<MealOption | null>(null);

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicatingOption, setDuplicatingOption] = useState<MealOption | null>(
    null,
  );
  const [duplicatingProgress, setDuplicatingProgress] =
    useState<DuplicatingProgress | null>(null);
  const [isDeletingOption, setIsDeletingOption] = useState(false);
  const [isDuplicatingOption, setIsDuplicatingOption] = useState(false);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isManageMealTimingsOpen, setIsManageMealTimingsOpen] = useState(false);
  const [isEditNutrientModalOpen, setIsEditNutrientModalOpen] = useState(false);
  const [isSavingNutrients, setIsSavingNutrients] = useState(false);
  // Latest adhoc plan version returned by getDetails — echoed back verbatim in
  // the edit-nutrients PATCH so the backend can detect stale updates.
  const [adhocPlanVersion, setAdhocPlanVersion] = useState<number | null>(null);
  const [clientPlayerId, setClientPlayerId] = useState<string | undefined>(undefined);
  const [approvalAction, setApprovalAction] = useState<"approve" | "reject" | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const [isSubmittingApprove, setIsSubmittingApprove] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const toastShownRef = useRef(false);

  

  const duplicateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const { can } = usePermissions();
  // When opening an existing plan (any planId other than the create route)
  // and the user can't edit meal plans, force the page into view-only mode
  // so Draft plans render the same way as Active ones.
  const isExistingPlanWithoutEditPermission =
    planId !== "create" && !can("meal_plan", "edit");
  // ── True when the plan is Active → everything becomes read-only ───────────
  const isViewOnly =
    mealPlan?.status === "Active" ||
    isDeactivated ||
    isApprovalMode ||
    isExistingPlanWithoutEditPermission;

  // Nutrient values edit is only allowed for Draft adhoc plans that the user can edit.
  const canEditNutrientValues =
    isAdhocMealMode && mealPlan?.status === "Draft" && !isViewOnly;

  // ── Approval mode handlers ────────────────────────────────────────────────
  const handleReject = () => {
    setRejectReason("");
    setIsRejectModalOpen(true);
  };

  const handleSubmitReject = async () => {
    if (!rejectReason.trim() || !planId) return;
    setIsSubmittingReject(true);
    try {
      const data = await rejectSuggestion(planId, {
        nutritionist_id: user?.nutrist_id,
        decline_reason: rejectReason.trim(),
      });
      if (data.success === true || data.success === "1" || data.success === 1) {
        setIsRejectModalOpen(false);
        navigate("/global-template");
        toast.success("Submitted successfully!", data.data?.message || "Template has been rejected.");
      } else {
        toast.error("Failed", data.message || "Could not reject template.");
      }
    } catch {
      toast.error("Error", "Failed to submit rejection.");
    } finally {
      setIsSubmittingReject(false);
    }
  };

  const handleApprove = () => {
    setIsApproveConfirmOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!planId) return;
    setIsSubmittingApprove(true);
    setApprovalAction("approve");
    try {
      const data = await approveSuggestion(planId, { nutritionist_id: user?.nutrist_id });
      setIsApproveConfirmOpen(false);
      navigate("/global-template");
      toast.success("Approved!", data.data?.message || "Template approved successfully.");
    } catch {
      setIsApproveConfirmOpen(false);
      navigate("/global-template");
    } finally {
      setIsSubmittingApprove(false);
    }
  };

  // ── Global template mode handlers ─────────────────────────────────────────
  const handleSaveAsDraft = () => navigate("/global-template");

  const handleSaveAsTemplate = async () => {
    if (!planId) return;
    try {
      const data = await publishGlobalTemplate(planId, {});
      if (data.success === true || data.success === "1" || data.success === 1) {
        navigate("/global-template");
        toast.success("Template published", data.message || "Template has been published successfully", 3000);
      } else {
        toast.error("Publish failed", data.message || "Failed to publish template", 3000);
      }
    } catch (err) {
      toast.error("Publish failed", err instanceof Error ? err.message : "An error occurred", 3000);
    }
  };

  const [isDownloadingAdhocPdf, setIsDownloadingAdhocPdf] = useState(false);
  const handleDownloadAdhocPdf = async () => {
    if (!planId || isDownloadingAdhocPdf) return;
    setIsDownloadingAdhocPdf(true);
    // Backend generates async — while generating it returns JSON with
    // status:"generating"; once ready it returns application/pdf bytes.
    // Poll every 2s (max ~30s) until the response content-type flips.
    const MAX_ATTEMPTS = 15;
    const POLL_MS = 2000;
    try {
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const res = await downloadAdhocMealPlan(planId);
        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          toast.error(
            "Download failed",
            errBody?.message || `Request failed (${res.status})`,
            3000,
          );
          return;
        }
        const contentType = res.headers.get("content-type") || "";

        // PDF ready — save as file
        if (contentType.includes("pdf") || contentType.includes("octet-stream")) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          const disposition = res.headers.get("content-disposition") || "";
          const nameMatch = /filename\*?=(?:UTF-8'')?"?([^"';]+)"?/i.exec(disposition);
          const suggestedName = nameMatch?.[1] ||
            `${mealPlan?.plan_name || `adhoc-plan-${planId}`}.pdf`;
          a.href = url;
          a.download = suggestedName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 0);
          toast.success("PDF ready", "Your PDF has been downloaded", 3000);
          return;
        }

        // Otherwise JSON — inspect status and either surface or keep polling
        const data = await res.json().catch(() => null);
        const ok = data?.success === true || data?.success === "1" || data?.success === 1;
        if (!ok) {
          toast.error("Download failed", data?.message || "Failed to download PDF", 3000);
          return;
        }
        const status = data?.data?.status;
        if (status && status !== "generating") {
          toast.success("PDF ready", data.message || "Your PDF is ready", 3000);
          return;
        }
        if (attempt === 0) {
          toast.warning(
            "Generating PDF",
            data?.message || "Your PDF is being prepared…",
            3000,
          );
        }
        await new Promise((res) => setTimeout(res, POLL_MS));
      }
      toast.warning(
        "Still generating",
        "The PDF is taking longer than expected. Please try again shortly.",
        4000,
      );
    } catch (err) {
      toast.error(
        "Download failed",
        err instanceof Error ? err.message : "An error occurred",
        3000,
      );
    } finally {
      setIsDownloadingAdhocPdf(false);
    }
  };

  // Save edited nutrient values on a Draft adhoc plan and refresh the details.
  const handleSaveNutrientValues = async (payload: EditNutrientValuesPayload) => {
    if (!planId || isSavingNutrients) return;
    setIsSavingNutrients(true);
    try {
      const requestBody: Record<string, unknown> = {
        ...payload,
        plan_name: mealPlan?.plan_name,
        meal_plan_type: mealPlan?.meal_type,
      };
      // Backend uses `version` for optimistic-concurrency: echo the latest
      // value from getDetails verbatim so it can detect stale updates.
      if (adhocPlanVersion !== null) {
        requestBody.version = adhocPlanVersion;
      }
      const data = await editAdhocMealPlanNutrientValues(planId, requestBody);
      if (data.success === true || data.success === "1" || data.success === 1) {
        setIsEditNutrientModalOpen(false);
        await refetchDetails(false);
        toast.success(
          "Nutrient values updated",
          data.message || "The nutrient values have been updated successfully",
          3000,
        );
      } else {
        toast.error(
          "Update failed",
          data.message || "Failed to update nutrient values",
          3000,
        );
      }
    } catch (err) {
      toast.error(
        "Update failed",
        err instanceof Error ? err.message : "An error occurred",
        3000,
      );
    } finally {
      setIsSavingNutrients(false);
    }
  };

  const [isPublishingAdhoc, setIsPublishingAdhoc] = useState(false);
  const handlePublishAdhoc = async () => {
    if (!planId || isPublishingAdhoc) return;

    // Validation: at least one meal option must exist across the plan.
    if (mealOptions.length === 0) {
      toast.warning(
        "No meal options",
        "Please add at least one meal option before publishing",
        3000,
      );
      return;
    }
    // Validation: every meal timing must have at least one option.
    const mealTimingsWithoutOptions = mealTimings.filter(
      (timing) =>
        !mealOptions.some((option) => option.mealTimingId === timing.id),
    );
    if (mealTimingsWithoutOptions.length > 0) {
      const mealNames = mealTimingsWithoutOptions
        .map((t) => t.mealType)
        .join(", ");
      toast.warning(
        "Incomplete meal plan",
        `Please add at least one option to: ${mealNames}`,
        4000,
      );
      return;
    }

    setIsPublishingAdhoc(true);
    try {
      const data = await publishAdhocMealPlan(planId);
      if (data.success === true || data.success === "1" || data.success === 1) {
        setMealPlan((prev) => (prev ? { ...prev, status: "Active" } : prev));
        toast.success(
          "Adhoc plan published",
          data.message || "The adhoc meal plan is now active",
          3000,
        );
      } else {
        toast.error("Publish failed", data.message || "Failed to publish adhoc plan", 3000);
      }
    } catch (err) {
      toast.error(
        "Publish failed",
        err instanceof Error ? err.message : "An error occurred",
        3000,
      );
    } finally {
      setIsPublishingAdhoc(false);
    }
  };

  // ── Drag and Drop ─────────────────────────────────────────────────────────
  const {
    getCardProps,
    getItemClassName,
    getMealTimingDropZoneProps: getMealTimingDropProps,
    shouldShowPlaceholder,
    handleDrop,
    isDragging,
  } = useMealOptionDragDrop({
    items: mealOptions,
    onReorder: (reorderedOptions) => {
      setMealOptions(reorderedOptions);
    },
    isDisabled: isViewOnly,
    mealPlanId: planId,
    isGlobalTemplateMode: isGlobalTemplateMode,
    isAdhocMealMode: isAdhocMealMode,
    mealTimings: mealTimings,
    onError: (error) => {
      toast.error("Move failed", error, 3000);
    },
    onSuccess: (message) => {
      toast.success("Option moved", message, 3000);
    },
    // OPTION 1: Remove onRefetch completely for instant UI
    // onRefetch: async () => {
    //   await fetchMealPlanDetails(false);
    // },

    // OPTION 2: Or use silent background refetch that doesn't show loading
    onRefetch: async () => {
      try {
        await refetchDetails(false);
      } catch (err) {
        console.error("Background sync failed:", err);
      }
    },
  });

  // Create a drop zone for empty meal timings
  const getMealTimingDropZoneProps = (mealTimingId: string) =>
    getMealTimingDropProps(mealTimingId);

  // Handler for precise drop positioning
  const handleDropAtPosition = (
    mealTimingId: string,
    beforeItemId: string | null,
    position: "top" | "bottom" | null,
  ) => {
    console.log("🎯 handleDropAtPosition:", {
      mealTimingId,
      beforeItemId,
      position,
    });
    handleDrop(mealTimingId, beforeItemId, position);
  };

  // ── Navigation toast ──────────────────────────────────────────────────────
  useEffect(() => {
    if (location.state?.showSuccessToast && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.success(
        "Meal plan created",
        "Your meal plan has been successfully created",
        4000,
      );
      // Clear the state after showing toast
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.showSuccessToast]);


  // ── Open preview modal ────────────────────────────────────────────────────
  const handleSendForApproval = () => {
    // Validation: Check if at least one meal option exists
    if (mealOptions.length === 0) {
      toast.warning(
        "No meal options",
        "Please add at least one meal option before sending to client",
        3000,
      );
      return;
    }

    // Validation: Check if all meal timings have at least one option
    const mealTimingsWithoutOptions = mealTimings.filter(
      (timing) =>
        !mealOptions.some((option) => option.mealTimingId === timing.id),
    );

    if (mealTimingsWithoutOptions.length > 0) {
      const mealNames = mealTimingsWithoutOptions
        .map((t) => t.mealType)
        .join(", ");
      toast.warning(
        "Incomplete meal plan",
        `Please add at least one option to: ${mealNames}`,
        4000,
      );
      return;
    }

    setIsPreviewModalOpen(true);
  };

  /**
   * Called by MealPlanPreviewModal after API returns success: "1".
   * Flip local status → "Active" so the UI immediately locks to view-only.
   */
  const handleConfirmSendToClient = () => {
    setIsPreviewModalOpen(false);
    setMealPlan((prev) => (prev ? { ...prev, status: "Active" } : prev));
    toast.success(
      "Sent to client",
      "The meal plan has been sent to the client",
      3000,
    );
  };

  // ── Clear glow after 4 s ──────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(
      () =>
        setMealOptions((prev) =>
          prev.map((o) => ({ ...o, isNewlyCreated: false })),
        ),
      1500, // CHANGED from 4000 to 1500ms
    );
    return () => clearTimeout(t);
  }, [mealOptions.length]);
  // ── Duplicate progress animation ──────────────────────────────────────────
  useEffect(() => {
    if (!duplicatingProgress) return;
    if (duplicatingProgress.progress < 100) {
      const t = setTimeout(
        () =>
          setDuplicatingProgress(
            (p) =>
              p ? { ...p, progress: Math.min(95, p.progress + 8) } : null, // +8 every 60ms ≈ ~700ms to 95%
          ),
        60, // was 100
      );
      return () => clearTimeout(t);
    }
    // Don't add the card here anymore since we're doing it in handleConfirmDuplicate
  }, [duplicatingProgress]);

  // ── Fetch global template details ────────────────────────────────────────
  const fetchGlobalTemplateDetails = async (showLoading = true) => {
    if (!planId) {
      setApiError("Missing template ID");
      setLoading(false);
      return;
    }
    try {
      if (showLoading) setLoading(true);
      setApiError(null);

      const api = await getGlobalTemplateDetails(planId);
      console.log("[GlobalTemplate] detail response:", api);

      if (api.success === true || api.success === "1" || api.success === "true") {
        const d = api.data;

        const statusMap: Record<string, MealPlan["status"]> = {
          draft:    "Draft",
          active:   "Active",
          inactive: "In-Active",
          pending:  "Approval Pending",
        };
        const navStatus = approvalState?.templateStatus?.toLowerCase();
        const status: MealPlan["status"] = statusMap[d.template_status?.toLowerCase()] ?? statusMap[navStatus ?? ""] ?? "Draft";

        if (d.nutritionist_profile) {
          setApiSubmitterPhoto(d.nutritionist_profile);
        }
        setMealPlan({
          id:         d.template_id,
          plan_name:  d.template_name || d.template_code || "",
          meal_type:  d.meal_plan_type || "",
          created_by: d.nutritionist_name || d.created_by_name || "",
          created_on: "",
          cal:        parseFloat(d.total_calories_max) || 0,
          adj_prot:   parseFloat(d.total_protein_max) || 0,
          carb:       parseFloat(d.total_carbs_max) || 0,
          fat:        parseFloat(d.total_fats_max) || 0,
          gl:         parseFloat(d.total_GL_max) || 0,
          status,
        });

        setNutritionData({
          total_cals_max:    parseFloat(d.total_calories_max) || 0,
          total_cals_min:    parseFloat(d.total_calories_min) || 0,
          cals_units:        "kcal",
          protein_max:       parseFloat(d.total_protein_max) || 0,
          protein_min:       parseFloat(d.total_protein_min) || 0,
          protein_units:     "g",
          carbs_max:         parseFloat(d.total_carbs_max) || 0,
          carbs_min:         parseFloat(d.total_carbs_min) || 0,
          carbs_units:       "g",
          fat_max:           parseFloat(d.total_fats_max) || 0,
          fat_min:           parseFloat(d.total_fats_min) || 0,
          fat_units:         "g",
          GL_max:            parseFloat(d.total_GL_max) || 0,
          GL_min:            parseFloat(d.total_GL_min) || 0,
          GL_units:          "g",
          CPF_max:           d.CPF_max || "0",
          CPF_min:           d.CPF_min || "0",
          current_cal:       0,
          current_protein:   0,
          current_carbs:     0,
          current_fats:      0,
          current_gl:        0,
          current_CPF:       "0:0:0",
        });

        const timings: MealTiming[] = [];
        const allOptions: MealOption[] = [];

        (d.meals ?? d.meal_details ?? []).forEach((meal: any) => {
          const mealTimingId = (meal.meal_id ?? meal.user_meal_type_id ?? "").toString();
          timings.push({
            id:         mealTimingId,
            mealType:   meal.meal_type_name ?? meal.user_meal_type ?? "",
            fk_user_meal: meal.meal_id ?? 0,
            time: {
              hour:   String(meal.hour ?? 8).padStart(2, "0"),
              minute: String(meal.minute ?? 0).padStart(2, "0"),
              period: meal.period ?? "AM",
            },
          });

          (meal.options ?? meal.Options ?? []).forEach((opt: any) => {
            const foodList = opt.foods ?? opt.food_data ?? opt.dishes ?? [];
            const hiddenNames: Set<string> = new Set(opt.hidden_dishes ?? []);
            allOptions.push({
              id:           (opt.option_id ?? opt.id ?? "").toString(),
              name:         opt.option_name ?? opt.name ?? "",
              mealTimingId,
              instructions: opt.option_instructions ?? opt.instructions ?? "",
              dishes: foodList.map((f: any) => ({
                id:        (f.food_id ?? f.meal_food_id ?? f.id ?? "").toString(),
                name:      f.fk_dish__dish_name ?? f.item_name ?? f.name ?? "",
                quantity:  String(f.quantity ?? 0),
                unit:      f.units ?? f.unit ?? "",
                oil:       parseFloat(f.oil) || 0,
                cal:       parseFloat(f.cals ?? f.cal) || 0,
                gl:        parseFloat(f.GL ?? f.gl) || 0,
                carb:      parseFloat(f.carbs ?? f.carb) || 0,
                protein:   parseFloat(f.AAFAdj_protein ?? f.protein) || 0,
                fat:       parseFloat(f.hidden_fat ?? f.fat) || 0,
                is_hidden: !!(f.is_hidden || f.hidden || hiddenNames.has(f.fk_dish__dish_name ?? f.item_name ?? f.name ?? "")),
              })),
              totals: {
                cal:     parseFloat(opt.total_cals ?? opt.option_total_cals ?? opt.total_cal) || 0,
                carb:    parseFloat(opt.total_carbs ?? opt.option_total_carbs ?? opt.total_carb) || 0,
                fat:     parseFloat(opt.total_fat ?? opt.option_total_fats ?? opt.total_fats) || 0,
                protein: parseFloat(opt.total_protein ?? opt.option_total_protein) || 0,
                gl:      parseFloat(opt.total_gl ?? opt.option_total_gl) || 0,
              },
              isNewlyCreated: false,
              optionNumber:   opt.option_no ?? opt.optionNumber ?? 1,
            });
          });
        });

        setMealTimings(timings);
        setMealOptions(allOptions);
      } else {
        setApiError(api.message || "Failed to fetch template details");
      }
    } catch (err) {
      setApiError(err instanceof Error ? err.message : "Failed to load template details");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // ── Fetch adhoc meal plan details ────────────────────────────────────────
  const fetchAdhocMealPlanDetails = async (showLoading = true) => {
    if (!planId) {
      setApiError("Missing adhoc plan ID");
      setLoading(false);
      return;
    }
    try {
      if (showLoading) setLoading(true);
      setApiError(null);

      const api = await getAdhocMealPlanDetails(planId);

      if (api.success === true || api.success === "1" || api.success === "true") {
        const d = api.data;

        const statusMap: Record<string, MealPlan["status"]> = {
          draft:    "Draft",
          active:   "Active",
          inactive: "In-Active",
          archived: "Archived",
        };
        const status: MealPlan["status"] = statusMap[String(d.status ?? "").toLowerCase()] ?? "Draft";

        setAdhocPlanVersion(
          typeof d.version === "number"
            ? d.version
            : d.version != null
              ? parseInt(String(d.version), 10) || null
              : null,
        );

        setMealPlan({
          id:         d.adhoc_plan_id,
          plan_name:  d.plan_name || d.plan_code || "",
          meal_type:  d.meal_plan_type || "",
          created_by: d.nutritionist_name || "",
          created_on: d.created_on || "",
          cal:        parseFloat(d.total_calories_max) || 0,
          adj_prot:   parseFloat(d.total_protein_max) || 0,
          carb:       parseFloat(d.total_carbs_max) || 0,
          fat:        parseFloat(d.total_fats_max) || 0,
          gl:         parseFloat(d.total_GL_max) || 0,
          status,
        });

        setNutritionData({
          total_cals_max:  parseFloat(d.total_calories_max) || 0,
          total_cals_min:  parseFloat(d.total_calories_min) || 0,
          cals_units:      d.total_calories_units || "kcal",
          protein_max:     parseFloat(d.total_protein_max) || 0,
          protein_min:     parseFloat(d.total_protein_min) || 0,
          protein_units:   d.total_protein_units || "g",
          carbs_max:       parseFloat(d.total_carbs_max) || 0,
          carbs_min:       parseFloat(d.total_carbs_min) || 0,
          carbs_units:     d.total_carbs_units || "g",
          fat_max:         parseFloat(d.total_fats_max) || 0,
          fat_min:         parseFloat(d.total_fats_min) || 0,
          fat_units:       d.total_fats_units || "g",
          GL_max:          parseFloat(d.total_GL_max) || 0,
          GL_min:          parseFloat(d.total_GL_min) || 0,
          GL_units:        d.total_GL_units || "g",
          CPF_max:         d.CPF_max || "0",
          CPF_min:         d.CPF_min || "0",
          current_cal:     0,
          current_protein: 0,
          current_carbs:   0,
          current_fats:    0,
          current_gl:      0,
          current_CPF:     "0:0:0",
        });

        const timings: MealTiming[] = [];
        const allOptions: MealOption[] = [];

        (d.meals ?? []).forEach((meal: any) => {
          const mealTimingId = String(meal.meal_id ?? "");
          timings.push({
            id:           mealTimingId,
            mealType:     meal.meal_type_name ?? "",
            fk_user_meal: meal.meal_id ?? 0,
            time: {
              hour:   String(meal.hour ?? 8).padStart(2, "0"),
              minute: String(meal.minute ?? 0).padStart(2, "0"),
              period: meal.period ?? "AM",
            },
          });

          (meal.options ?? []).forEach((opt: any) => {
            const foodList = opt.foods ?? [];
            const hiddenNames: Set<string> = new Set(opt.hidden_dishes ?? []);
            allOptions.push({
              id:           String(opt.option_id ?? ""),
              name:         opt.option_name ?? "",
              mealTimingId,
              instructions: opt.option_instructions ?? "",
              dishes: foodList.map((f: any) => ({
                id:        String(f.food_id ?? ""),
                name:      f.fk_dish__dish_name ?? f.item_name ?? "",
                quantity:  String(f.quantity ?? 0),
                unit:      f.units ?? "",
                oil:       parseFloat(f.oil) || 0,
                cal:       parseFloat(f.cals) || 0,
                gl:        parseFloat(f.GL) || 0,
                carb:      parseFloat(f.carbs) || 0,
                protein:   parseFloat(f.AAFAdj_protein) || 0,
                fat:       parseFloat(f.hidden_fat) || 0,
                is_hidden: !!(f.is_hidden || hiddenNames.has(f.fk_dish__dish_name ?? f.item_name ?? "")),
              })),
              totals: {
                cal:     parseFloat(opt.total_cals) || 0,
                carb:    parseFloat(opt.total_carbs) || 0,
                fat:     parseFloat(opt.total_fat) || 0,
                protein: parseFloat(opt.total_aadjprotein) || 0,
                gl:      parseFloat(opt.total_gl) || 0,
              },
              isNewlyCreated: false,
              optionNumber:   opt.option_no ?? 1,
            });
          });
        });

        setMealTimings(timings);
        setMealOptions(allOptions);
      } else {
        setApiError(api.message || "Failed to fetch adhoc meal plan details");
      }
    } catch (err) {
      setApiError(err instanceof Error ? err.message : "Failed to load adhoc meal plan details");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const fetchSuggestionDetails = async (showLoading = true) => {
    if (!planId) { setApiError("Missing suggestion ID"); setLoading(false); return; }
    try {
      if (showLoading) setLoading(true);
      setApiError(null);
      const api = await getSuggestedPlanGlobalTemplateDetails(planId);
      if (api.success === true || api.success === "1" || api.success === "true") {
        const d = api.data;
        setMealPlan({
          id:         d.plan_id,
          plan_name:  d.suggested_template_name || d.meal_plan_name || d.plan_code || "",
          meal_type:  d.meal_plan_type || "",
          created_by: d.submitted_by_name || "",
          created_on: "",
          cal:        parseFloat(d.total_calories_max) || 0,
          adj_prot:   parseFloat(d.total_protein_max) || 0,
          carb:       parseFloat(d.total_carbs_max) || 0,
          fat:        parseFloat(d.total_fats_max) || 0,
          gl:         parseFloat(d.total_GL_max) || 0,
          status:     "Draft",
        });
        setGeneratedTemplateId(d.generated_template_id ?? null);
        setGeneratedTemplateCode(d.generated_template_code || "");
        if (d.submitted_by_photo) {
          setApiSubmitterPhoto(d.submitted_by_photo);
        }
        if (d.decline_reason || d.reviewed_by_name) {
          setApiRejectionMeta({
            reason:         d.decline_reason || "",
            reviewedByName: d.reviewed_by_name || "",
            reviewedByRole: d.reviewed_by_role || "",
          });
        }
        setNutritionData({
          total_cals_max:  parseFloat(d.total_calories_max) || 0,
          total_cals_min:  parseFloat(d.total_calories_min) || 0,
          cals_units:      d.total_calories_units || "kcal",
          protein_max:     parseFloat(d.total_protein_max) || 0,
          protein_min:     parseFloat(d.total_protein_min) || 0,
          protein_units:   "g",
          carbs_max:       parseFloat(d.total_carbs_max) || 0,
          carbs_min:       parseFloat(d.total_carbs_min) || 0,
          carbs_units:     "g",
          fat_max:         parseFloat(d.total_fats_max) || 0,
          fat_min:         parseFloat(d.total_fats_min) || 0,
          fat_units:       "g",
          GL_max:          parseFloat(d.total_GL_max) || 0,
          GL_min:          parseFloat(d.total_GL_min) || 0,
          GL_units:        "g",
          CPF_max:         d.CPF_max || "0",
          CPF_min:         d.CPF_min || "0",
          current_cal:     0,
          current_protein: 0,
          current_carbs:   0,
          current_fats:    0,
          current_gl:      0,
          current_CPF:     "0:0:0",
        });
        const timings: MealTiming[] = [];
        const allOptions: MealOption[] = [];
        (d.meals ?? []).forEach((meal: any) => {
          const mealTimingId = String(meal.meal_id ?? "");
          timings.push({
            id: mealTimingId,
            mealType: meal.meal_type_name ?? "",
            fk_user_meal: meal.meal_id ?? 0,
            time: {
              hour:   String(meal.hour ?? 8).padStart(2, "0"),
              minute: String(meal.minute ?? 0).padStart(2, "0"),
              period: meal.period ?? "AM",
            },
          });
          (meal.options ?? []).forEach((opt: any) => {
            const foodList = opt.foods ?? opt.food_data ?? [];
            const hiddenNames: Set<string> = new Set(opt.hidden_dishes ?? []);
            allOptions.push({
              id:           String(opt.option_id ?? ""),
              name:         opt.option_name ?? "",
              mealTimingId,
              instructions: opt.option_instructions ?? "",
              dishes: foodList.map((f: any) => ({
                id:        String(f.meal_food_id ?? f.food_id ?? ""),
                name:      f.fk_dish__dish_name ?? f.item_name ?? "",
                quantity:  String(f.quantity ?? 0),
                unit:      f.units ?? "",
                oil:       parseFloat(f.oil) || 0,
                cal:       parseFloat(f.cals ?? f.cal) || 0,
                gl:        parseFloat(f.GL ?? f.gl) || 0,
                carb:      parseFloat(f.carbs ?? f.carb) || 0,
                protein:   parseFloat(f.AAFAdj_protein ?? f.protein) || 0,
                fat:       parseFloat(f.hidden_fat ?? f.fat) || 0,
                is_hidden: !!(f.is_hidden || f.hidden || hiddenNames.has(f.fk_dish__dish_name ?? f.item_name ?? "")),
              })),
              totals: {
                cal:     parseFloat(opt.total_cals ?? opt.option_total_cals) || 0,
                carb:    parseFloat(opt.total_carbs ?? opt.option_total_carbs) || 0,
                fat:     parseFloat(opt.total_fat ?? opt.total_fats ?? opt.option_total_fats) || 0,
                protein: parseFloat(opt.total_aadjprotein ?? opt.total_protein ?? opt.option_total_protein) || 0,
                gl:      parseFloat(opt.total_gl ?? opt.option_total_gl) || 0,
              },
              isNewlyCreated: false,
              optionNumber:   opt.option_no ?? 1,
            });
          });
        });
        setMealTimings(timings);
        setMealOptions(allOptions);
      } else {
        setApiError(api.message || "Failed to fetch suggestion details");
      }
    } catch (err) {
      setApiError(err instanceof Error ? err.message : "Failed to load suggestion details");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchMealPlanDetails = async (showLoading = true) => {
    if (!userId || !planId) {
      setApiError("Missing user ID or plan ID");
      setLoading(false);
      return;
    }
    try {
      if (showLoading) setLoading(true);
      setApiError(null);
      const api = await getMealPlanDetail({ user_id: userId, meal_plan_id: planId });

      if (api.success === "1") {
        const statusMap: Record<string, MealPlan["status"]> = {
          "1": "Approval Pending",
          "2": "Active",
          "3": "In-Active",
        };
        const status: MealPlan["status"] =
          statusMap[api.meal_status] ?? "Draft";

        setMealPlan({
          id: api.meal_plan_id,
          plan_name: api.plan_code || api.meal_plan_name,
          meal_type: api.meal_plan_type,
          created_by: "",
          created_on: "",
          cal: parseFloat(api.nutrient_max_values.total_calories_max) || 0,
          adj_prot: parseFloat(api.nutrient_max_values.total_protein_max) || 0,
          carb: parseFloat(api.nutrient_max_values.total_carbs_max) || 0,
          fat: parseFloat(api.nutrient_max_values.total_fats_max) || 0,
          gl: parseFloat(api.nutrient_max_values.total_GL_max) || 0,
          status,
        });

        setNutritionData({
          total_cals_max:
            parseFloat(api.nutrient_max_values.total_calories_max) || 0,
          total_cals_min:
            parseFloat(api.nutrient_max_values.total_calories_min) || 0,
          cals_units: api.nutrient_max_values.total_calories_units || "kcal",
          protein_max:
            parseFloat(api.nutrient_max_values.total_protein_max) || 0,
          protein_min:
            parseFloat(api.nutrient_max_values.total_protein_min) || 0,
          protein_units: api.nutrient_max_values.total_protein_units || "g",
          carbs_max: parseFloat(api.nutrient_max_values.total_carbs_max) || 0,
          carbs_min: parseFloat(api.nutrient_max_values.total_carbs_min) || 0,
          carbs_units: api.nutrient_max_values.total_carbs_units || "g",
          fat_max: parseFloat(api.nutrient_max_values.total_fats_max) || 0,
          fat_min: parseFloat(api.nutrient_max_values.total_fats_min) || 0,
          fat_units: api.nutrient_max_values.total_fats_units || "g",
          GL_max: parseFloat(api.nutrient_max_values.total_GL_max) || 0,
          GL_min: parseFloat(api.nutrient_max_values.total_GL_min) || 0,
          GL_units: api.nutrient_max_values.total_GL_units || "g",
          CPF_max: api.nutrient_max_values.CPF_max || "0",
          CPF_min: api.nutrient_max_values.CPF_min || "0",
          current_cal: parseFloat(api.final_cal_avg) || 0,
          current_protein: parseFloat(api.final_protein_avg) || 0,
          current_carbs: parseFloat(api.final_carbs_avg) || 0,
          current_fats: parseFloat(api.final_fats_avg) || 0,
          current_gl: parseFloat(api.final_gl_avg) || 0,
          current_CPF: api.CPF || "0:0:0",
        });

        const timings: MealTiming[] = [];
        const allOptions: MealOption[] = [];
        // hidden_dishes is a top-level array of item_name strings
        const hiddenDishNames: Set<string> = new Set(api.hidden_dishes ?? []);

        api.meal_plan_data.forEach((mealData: ApiMealPlanData) => {
          const [hourStr, minuteStr] = mealData.meal_time
            ? mealData.meal_time.split(":") // original: parse "HH:MM:SS"
            : [
                `${mealData.hour ?? 12}`,
                String(mealData.minute ?? 0).padStart(2, "0"),
              ]; // NEW: pre-parsed fields
          let hour = parseInt(hourStr, 10);
          const period = mealData.period ?? (hour >= 12 ? "PM" : "AM"); // NEW: use period if provided, else derive (original logic)
          if (hour > 12) hour -= 12;
          if (hour === 0) hour = 12;
          const mealTimingId = mealData.user_meal_type_id.toString();
          timings.push({
            id: mealTimingId,
            mealType: mealData.user_meal_type,
            fk_user_meal: mealData.fk_user_meal,
            time: {
              hour: hour.toString().padStart(2, "0"),
              minute: minuteStr,
              period,
            },
          });
          (mealData.Options ?? []).forEach((apiOption: any) => {
            allOptions.push({
              id: apiOption.option_id.toString(),
              name: apiOption.option_name,
              mealTimingId,
              instructions: apiOption.option_instructions || "",
              dishes: apiOption.food_data.map((f: any) => ({
                id: f.meal_food_id.toString(),
                name: f.item_name,
                quantity: f.quantity,
                unit: f.units,
                oil: parseFloat(f.oil) || 0,
                cal: parseFloat(f.cals) || 0,
                gl: parseFloat(f.GL) || 0,
                carb: parseFloat(f.carbs) || 0,
                protein: parseFloat(f.AAFAdj_protein) || 0,
                fat: parseFloat(f.hidden_fat) || 0,
                is_hidden: hiddenDishNames.has(f.item_name),
              })),
              totals: {
                cal: parseFloat(apiOption.option_total_cals) || 0,
                carb: parseFloat(apiOption.option_total_carbs) || 0,
                fat: parseFloat(apiOption.option_total_fats) || 0,
                protein: parseFloat(apiOption.option_total_protein) || 0,
                gl: parseFloat(apiOption.option_total_gl) || 0,
              },
              isNewlyCreated: false,
              optionNumber: apiOption.option_no,
            });
          });
        });

        setMealTimings(timings);
        setMealOptions(allOptions);
      } else {
        setApiError(api.message || "Failed to fetch meal plan details");
      }
    } catch (err) {
      setApiError(
        err instanceof Error ? err.message : "Failed to load meal plan details",
      );
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  // Refetch dispatcher — routes to the fetcher matching the current mode.
  // Every "refresh after mutation" call site must go through this so we never
  // hit the meal-plan fetcher (which requires userId) in adhoc/global/suggestion
  // mode.
  const refetchDetails = async (showLoading = false) => {
    if (isSuggestionMode) return fetchSuggestionDetails(showLoading);
    if (isGlobalTemplateMode) return fetchGlobalTemplateDetails(showLoading);
    if (isAdhocMealMode) return fetchAdhocMealPlanDetails(showLoading);
    return fetchMealPlanDetails(showLoading);
  };

  useEffect(() => {
    if (isAdhocMealMode) {
      // Freshly-created plans navigate here from MealPlanCreatePage with a
      // client-side mockAdhocId (Date.now()) and isNewPlan: true — no backend
      // record yet, so hydrate from location.state instead of hitting the API.
      if (approvalState?.isNewPlan) {
        const passedPlan = approvalState?.mealPlan;
        const passedTimings = approvalState?.mealTimings ?? [];
        if (passedPlan) {
          setMealPlan(passedPlan);
        } else {
          setMealPlan({
            id: parseInt(planId ?? "0", 10),
            plan_name: "Adhoc plan",
            meal_type: "Regular",
            created_by: "You",
            created_on: new Date().toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            }),
            cal: 0,
            adj_prot: 0,
            carb: 0,
            fat: 0,
            gl: 0,
            status: "Draft",
          });
        }
        setMealTimings(
          passedTimings.map((t, i) => ({
            id: t.id,
            mealType: t.mealType,
            time: t.time,
            fk_user_meal: i + 1,
          })),
        );
        setMealOptions([]);
        setLoading(false);
        return;
      }
      fetchAdhocMealPlanDetails();
      return;
    }
    if (isSuggestionMode) {
      fetchSuggestionDetails();
    } else if (isGlobalTemplateMode) {
      fetchGlobalTemplateDetails();
    } else {
      fetchMealPlanDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, isGlobalTemplateMode, isSuggestionMode, isAdhocMealMode]);

  // ── Fetch client's OneSignal playerId from Firebase ────────────────────────
  useEffect(() => {
    if (!userId) return;
    const fetchPlayerId = async () => {
      try {
        const userDocRef = doc(db, FIREBASE_COLLECTIONS.USERS, `userid_${userId}`);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setClientPlayerId(userDoc.data()?.playerId || undefined);
        }
      } catch (err) {
        console.error("Failed to fetch OneSignal playerId:", err);
      }
    };
    fetchPlayerId();
  }, [userId]);

  //Dispatch event when navigating away to refresh MealPlanTab in background
  useEffect(() => {
    return () => {
      // Dispatch event on unmount (when navigating back to Plans page)
      console.log("🚀 Dispatching mealPlanUpdated event on component unmount");
      window.dispatchEvent(new CustomEvent("mealPlanUpdated"));
    };
  }, []);

  // ── Recalculate displayed nutrition stats whenever options change ──────────
  useEffect(() => {
    // Nothing to calculate if the API hasn't hydrated nutritionData yet,
    // or if there are no meal timings defined.
    if (!nutritionData || mealTimings.length === 0) return;

    // Build a CPF formatter using the project's CPFCalculator, same as
    // MealPlanPreviewModal does with its local `calcCPF` helper.
    const formatCPF = (
      carb: number,
      protein: number,
      fat: number,
      cal: number,
    ): string => {
      try {
        const result = calculateCPFRatio(carb, protein, fat, cal);
        return formatCPFRatio(result.ratio);
      } catch {
        // Fallback if CPFCalculator throws (e.g. zero calories)
        return "0:0:0";
      }
    };

    const updated = calcLocalCurrentNutrition(
      mealTimings,
      mealOptions,
      formatCPF,
    );

    // Spread only the current_* fields; max/min/units stay as-is from the API
    setNutritionData((prev) => (prev ? { ...prev, ...updated } : prev));

    // We intentionally omit `nutritionData` from the dep-array to avoid a loop;
    // the functional-update form of setNutritionData handles stale-closure safely.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mealOptions, mealTimings]);


  // ── Handlers ──────────────────────────────────────────────────────────────

  const getNextOptionNumber = (mealTimingId: string) =>
    mealOptions.filter((o) => o.mealTimingId === mealTimingId).length + 1;

  const handleCreateClick = (
    mealType: string,
    mealTimingId: string,
    userMealId: number,
  ) => {
    if (isViewOnly) return;
    setSelectedMealType(mealType);
    setSelectedMealTimingId(mealTimingId);
    setSelectedUserMealId(userMealId);
    setIsEditMode(false);
    setEditingOptionData(undefined);
    setIsCreateModalOpen(true);
  };

  const handleEditOption = (option: MealOption) => {
    if (isViewOnly) return;

    // Guard: a temp ID is a pure numeric timestamp (13 digits).
    // Real DB IDs come back from the API and are assigned an optionNumber.
    if (!option.optionNumber) {
      toast.warning(
        "Still syncing",
        "This option is still syncing with the server. Please wait a moment and try again.",
        3000,
      );
      refetchDetails(false);
      return;
    }

    const timing = mealTimings.find((m) => m.id === option.mealTimingId);
    if (timing) {
      setSelectedMealType(timing.mealType);
      setSelectedMealTimingId(option.mealTimingId);
      setSelectedUserMealId(timing.fk_user_meal);
    }
    setEditingOptionData({
      id: option.id,
      name: option.name,
      dishes: option.dishes,
      instructions: option.instructions,
      optionNumber: option.optionNumber,
      totals: option.totals,
    });
    setIsEditMode(true);
    setIsViewModalOpen(false);
    setIsCreateModalOpen(true);
  };


  const handleCreateMealOption = (
    optionName: string,
    dishes: DishItem[],
    instructions: string,
    totals: {
      cal: number;
      carb: number;
      fat: number;
      protein: number;
      gl: number;
    },
  ) => {
    setMealOptions((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        name: optionName,
        mealTimingId: selectedMealTimingId,
        dishes,
        instructions,
        totals,
        isNewlyCreated: true,
        optionNumber: getNextOptionNumber(selectedMealTimingId),
      },
    ]);
    // ── KEY FIX: silently replace the temp Date.now() ID with the real DB ID
    // so that subsequent Edit / Duplicate calls have a valid option_id.
    refetchDetails(false);
  };
  const handleUpdateMealOption = (
    optionId: string,
    optionName: string,
    dishes: DishItem[],
    instructions: string,
    totals: {
      cal: number;
      carb: number;
      fat: number;
      protein: number;
      gl: number;
    },
  ) => {
    setMealOptions((prev) =>
      prev.map((o) =>
        o.id === optionId
          ? {
              ...o,
              name: optionName,
              dishes,
              instructions,
              totals,
              isNewlyCreated: true,
            }
          : o,
      ),
    );
  };

  const handleDeleteMealOption = async () => {
    if (isViewOnly || !selectedViewOption || !planId) return;
    setIsDeletingOption(true);
    try {
      let optionNumber = selectedViewOption.optionNumber;
      if (!optionNumber) {
        const siblings = mealOptions.filter(
          (o) => o.mealTimingId === selectedViewOption.mealTimingId,
        );
        optionNumber =
          siblings.findIndex((o) => o.id === selectedViewOption.id) + 1 || 1;
      }
      let result: any;
      if (isGlobalTemplateMode) {
        result = await deleteGlobalTemplateMealOptions(planId!, {
          option_no: optionNumber,
          meal_id: parseInt(selectedViewOption.mealTimingId),
        });
      } else if (isAdhocMealMode) {
        result = await deleteAdhocMealPlanOption(planId!, {
          option_no: optionNumber,
          option_id: parseInt(selectedViewOption.id, 10),
          meal_id: parseInt(selectedViewOption.mealTimingId),
        });
      } else {
        result = await deleteMealCardPlan({
          option_no: optionNumber,
          meal_type_id: parseInt(selectedViewOption.mealTimingId),
          meal_plan_id: parseInt(planId),
        });
      }
      if (result.success === true || result.success === "1" || result.success === 1) {
        setMealOptions((prev) =>
          prev.filter((o) => o.id !== selectedViewOption.id),
        );
        setIsViewModalOpen(false);
        setSelectedViewOption(null);
        toast.success(
          "Option deleted",
          result.message || `${selectedViewOption.name} has been removed`,
          3000,
        );
      } else {
        toast.error(
          "Delete failed",
          result.message || "Failed to delete",
          3000,
        );
      }
    } catch (err) {
      toast.error(
        "Delete failed",
        err instanceof Error ? err.message : "An error occurred",
        3000,
      );
    } finally {
      setIsDeletingOption(false);
    }
  };

   const handleDuplicateOption = (option: MealOption) => {
    if (isViewOnly) return;

    // Guard: block duplicate until the real DB ID is available
    if (!option.optionNumber) {
      toast.warning(
        "Still syncing",
        "This option is still syncing with the server. Please wait a moment and try again.",
        3000,
      );
      refetchDetails(false);
      return;
    }

    setDuplicatingOption(option);
    setIsDuplicatingOption(false); // Reset before opening modal
    setIsDuplicateModalOpen(true);
  };

const handleConfirmDuplicate = async (newName: string) => {
  if (!duplicatingOption || !planId) return;

  setIsDuplicateModalOpen(false);
  if (duplicateTimeoutRef.current) {
    clearTimeout(duplicateTimeoutRef.current);
    duplicateTimeoutRef.current = null;
  }

  // Snapshot the source option at the moment of click — survives async refetches
  // that may replace temp Date.now() IDs with real DB IDs.
  const sourceOption = {
    ...duplicatingOption,
    _snapshotIndex: mealOptions
      .filter((o) => o.mealTimingId === duplicatingOption.mealTimingId)
      .findIndex((o) => o.id === duplicatingOption.id),
  };

  // ── Helper: resolve insertion index robustly ─────────────────────────────
  // After a refetch the original temp id may no longer exist, so we fall back
  // to optionNumber match, then the snapshot index captured above.
  const resolveSourceIndex = (forMeal: MealOption[]): number => {
    // 1. Exact id match (works when option was created from DB, not a temp id)
    const byId = forMeal.findIndex((o) => o.id === sourceOption.id);
    if (byId !== -1) return byId;

    // 2. optionNumber match (survives id replacement after refetch)
    if (sourceOption.optionNumber != null) {
      const byOptionNo = forMeal.findIndex(
        (o) => o.optionNumber === sourceOption.optionNumber,
      );
      if (byOptionNo !== -1) return byOptionNo;
    }

    // 3. Name + not the new duplicate (last resort)
    const byName = forMeal.findIndex(
      (o) => o.name === sourceOption.name && o.id !== sourceOption.id,
    );
    if (byName !== -1) return byName;

    // 4. Snapshot index captured before any async gap
    if (
      sourceOption._snapshotIndex !== -1 &&
      sourceOption._snapshotIndex < forMeal.length
    ) {
      return sourceOption._snapshotIndex;
    }

    // 5. Ultimate fallback: insert at end
    return forMeal.length - 1;
  };

  // ── Helper: reorder mealOptions after duplication ────────────────────────
  const insertDuplicateIntoOptions = (
    prev: MealOption[],
    newOptionId: string,
  ): MealOption[] => {
    if (!newOptionId) return prev;

    const forMeal = prev.filter(
      (o) => o.mealTimingId === sourceOption.mealTimingId,
    );
    const others = prev.filter(
      (o) => o.mealTimingId !== sourceOption.mealTimingId,
    );
    const newOpt = forMeal.find((o) => o.id === newOptionId);

    if (!newOpt) return prev; // new option not yet in state — no-op

    const srcIdx = resolveSourceIndex(
      forMeal.filter((o) => o.id !== newOptionId), // exclude the new card itself when searching
    );

    const without = forMeal.filter((o) => o.id !== newOptionId);
    const reordered = [
      ...without.slice(0, srcIdx + 1),
      { ...newOpt, isNewlyCreated: true },
      ...without.slice(srcIdx + 1),
    ];

    return [...others, ...reordered];
  };

  // ── Horizontal scroll to the source card's column ────────────────────────
  const sourceCardElement = document.querySelector(
    `[data-option-id="${sourceOption.id}"]`,
  ) as HTMLElement;

  if (sourceCardElement && scrollContainerRef.current) {
    const mealTimingCard = sourceCardElement.closest(
      ".meal-timing-card-container",
    ) as HTMLElement;
    if (mealTimingCard) {
      const container = scrollContainerRef.current;
      const cardRect = mealTimingCard.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const cardCenter =
        cardRect.left -
        containerRect.left +
        container.scrollLeft +
        cardRect.width / 2;
      container.scrollTo({
        left: cardCenter - containerRect.width / 2,
        behavior: "smooth",
      });
    }
  }

  // ── Fire API immediately (parallel with scroll) ───────────────────────────
  let apiResult: {
    success: string;
    message?: string;
    data?: { option_id: number };
  } | null = null;
  let apiError: Error | null = null;

  try {
    if (isGlobalTemplateMode) {
      apiResult = await duplicateGlobalTemplateMealOptions({
        template_id: parseInt(planId),
        source_option_id: parseInt(sourceOption.id),
        new_option_name: newName,
      });
    } else if (isAdhocMealMode) {
      apiResult = await duplicateAdhocMealPlanOption({
        adhoc_plan_id: parseInt(planId),
        source_option_id: parseInt(sourceOption.id),
        new_option_name: newName,
      });
    } else {
      apiResult = await getDuplicateMealOptionDetails({
        meal_plan_id: parseInt(planId),
        source_option_id: parseInt(sourceOption.id),
        new_option_name: newName,
      });
    }
  } catch (err) {
    apiError = err instanceof Error ? err : new Error("An error occurred");
  }

  // ── After scroll settles, show progress bar (or surface error) ────────────
  duplicateTimeoutRef.current = setTimeout(() => {
    duplicateTimeoutRef.current = null;

    // ── API failed ───────────────────────────────────────────────────────────
    if (apiError || !apiResult) {
      toast.error(
        "Duplication failed",
        apiError?.message ?? "Failed to duplicate option",
        3000,
      );
      cancelDuplication();
      return;
    }
    if (apiResult.success !== "1") {
      toast.error(
        "Duplication failed",
        apiResult.message || "Failed to duplicate option",
        3000,
      );
      cancelDuplication();
      return;
    }

    const newOptionId = apiResult.data?.option_id?.toString() ?? "";

    // ── Shared completion logic ──────────────────────────────────────────────
    const driveToCompletion = async (delayBeforeStart: number) => {
      if (delayBeforeStart > 0) {
        await new Promise((res) => setTimeout(res, delayBeforeStart));
      }

      // Show progress bar
      setIsDuplicatingOption(true);
      setDuplicatingProgress({
        mealTimingId: sourceOption.mealTimingId,
        progress: 0,
        newName,
        sourceOption,
      });

      await new Promise((res) => setTimeout(res, 700)); // let bar animate
      setDuplicatingProgress((p) => (p ? { ...p, progress: 100 } : null));
      await new Promise((res) => setTimeout(res, 600)); // show 100% briefly

      // Fetch until the new option appears in state
      if (newOptionId) {
        await fetchUntilOptionExists(newOptionId);
      } else {
        await refetchDetails(false);
      }

      // Reorder: place duplicate immediately after its source
      if (newOptionId) {
        setMealOptions((prev) => {
          const reordered = insertDuplicateIntoOptions(prev, newOptionId);

          // If the original was deleted by the backend (e.g. rename-style API),
          // restore it from the snapshot so the user sees both options.
          const originalStillPresent = reordered.some(
            (o) => o.id === sourceOption.id && o.id !== newOptionId,
          );
          if (!originalStillPresent && isGlobalTemplateMode) {
            const forMeal = reordered.filter(
              (o) => o.mealTimingId === sourceOption.mealTimingId,
            );
            const others = reordered.filter(
              (o) => o.mealTimingId !== sourceOption.mealTimingId,
            );
            const copyIdx = forMeal.findIndex((o) => o.id === newOptionId);
            const restored = { ...sourceOption, isNewlyCreated: false };
            const withOriginal = [...forMeal];
            withOriginal.splice(Math.max(0, copyIdx), 0, restored);
            return [...others, ...withOriginal];
          }

          return reordered;
        });
      }

      setDuplicatingProgress(null);
      setIsDuplicatingOption(false);
      setDuplicatingOption(null);
      toast.success(
        "Option duplicated",
        apiResult!.message || `${newName} has been created successfully`,
        3000,
      );
    };

    // ── Decide whether we need to scroll the options container first ──────────
    const sourceCardEl = document.querySelector(
      `[data-option-id="${sourceOption.id}"]`,
    ) as HTMLElement;

    if (sourceCardEl) {
      const optionsContainer = sourceCardEl.closest(
        ".meal-options-container",
      ) as HTMLElement;

      if (optionsContainer) {
        const containerRect = optionsContainer.getBoundingClientRect();
        const cardRect = sourceCardEl.getBoundingClientRect();
        const cardBottomRel =
          cardRect.bottom - containerRect.top + optionsContainer.scrollTop;
        const targetScrollTop =
          cardBottomRel + 158 - optionsContainer.clientHeight;
        const isScrollNeeded = targetScrollTop > 0;
        const isLastCard =
          targetScrollTop >
          optionsContainer.scrollHeight - optionsContainer.clientHeight;

        if (!isScrollNeeded) {
          // Card already visible — start immediately
          driveToCompletion(0);
        } else if (isLastCard) {
          // Card is at the very bottom — show bar first, then scroll
          setIsDuplicatingOption(true);
          setDuplicatingProgress({
            mealTimingId: sourceOption.mealTimingId,
            progress: 0,
            newName,
            sourceOption,
          });
          setTimeout(
            () =>
              optionsContainer.scrollTo({
                top: optionsContainer.scrollHeight,
                behavior: "smooth",
              }),
            50,
          );

          (async () => {
            await new Promise((res) => setTimeout(res, 700));
            setDuplicatingProgress((p) => (p ? { ...p, progress: 100 } : null));
            await new Promise((res) => setTimeout(res, 600));

            if (newOptionId) {
              await fetchUntilOptionExists(newOptionId);
            } else {
              await refetchDetails(false);
            }

            if (newOptionId) {
              setMealOptions((prev) => {
                const reordered = insertDuplicateIntoOptions(prev, newOptionId);
                const originalStillPresent = reordered.some(
                  (o) => o.id === sourceOption.id && o.id !== newOptionId,
                );
                if (!originalStillPresent && isGlobalTemplateMode) {
                  const forMeal = reordered.filter(
                    (o) => o.mealTimingId === sourceOption.mealTimingId,
                  );
                  const others = reordered.filter(
                    (o) => o.mealTimingId !== sourceOption.mealTimingId,
                  );
                  const copyIdx = forMeal.findIndex((o) => o.id === newOptionId);
                  const restored = { ...sourceOption, isNewlyCreated: false };
                  const withOriginal = [...forMeal];
                  withOriginal.splice(Math.max(0, copyIdx), 0, restored);
                  return [...others, ...withOriginal];
                }
                return reordered;
              });
            }

            setDuplicatingProgress(null);
            setIsDuplicatingOption(false);
            setDuplicatingOption(null);
            toast.success(
              "Option duplicated",
              apiResult!.message || `${newName} has been created successfully`,
              3000,
            );
          })();
        } else {
          // Scroll into view, then start after animation settles
          optionsContainer.scrollTo({
            top: targetScrollTop,
            behavior: "smooth",
          });
          driveToCompletion(400);
        }
        return;
      }
    }

    // Fallback: no card element found — just run immediately
    driveToCompletion(0);
  }, 800);
};

const cancelDuplication = () => {
  if (duplicateTimeoutRef.current) {
    clearTimeout(duplicateTimeoutRef.current);
    duplicateTimeoutRef.current = null;
  }
  setDuplicatingProgress(null);
  setIsDuplicatingOption(false);
  setDuplicatingOption(null);
};

const fetchUntilOptionExists = async (
  newOptionId: string,
  maxAttempts = 4,
  delayMs = 600,
): Promise<void> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await refetchDetails(false);
    const found = await new Promise<boolean>((resolve) => {
      setTimeout(() => {
        setMealOptions((prev) => {
          resolve(prev.some((o) => o.id === newOptionId));
          return prev; // no-op read
        });
      }, 50);
    });
    if (found) return;
    if (attempt < maxAttempts - 1) {
      await new Promise((res) => setTimeout(res, delayMs));
    }
  }
};


  const handleUpdateMealTimings = async (updated: MealTiming[]) => {
    setMealTimings(updated);
    // Refetch so existingMealTimings has correct fk_user_meal IDs for next edit
    await refetchDetails(false);
  };

  // ── Guard ─────────────────────────────────────────────────────────────────
  // Global template / adhoc modes only need planId (no userId in the route)
  if (!planId || (!userId && !isGlobalTemplateMode && !isSuggestionMode && !isAdhocMealMode)) {
    return (
      <div className="min-h-screen bg-[#F7F7FA] flex items-center justify-center">
        <div className="text-center">
          <p className="text-base text-red-500 mb-2">Invalid URL parameters</p>
          <Button
            variant="primary"
            onClick={() =>
              isGlobalTemplateMode
                ? navigate("/global-template")
                : isAdhocMealMode
                  ? navigate("/adhoc-meal")
                  : navigate(`/client/${userId}/plans`)
            }
            className="mt-4"
          >
            {isGlobalTemplateMode
              ? "Back to Templates"
              : isAdhocMealMode
                ? "Back to Adhoc Plans"
                : "Back to Plans"}
          </Button>
        </div>
      </div>
    );
  }

  const userIdNumber = parseInt(userId ?? "0", 10);
  const getStatusColor = (s: string) =>
    ({
      Active: "bg-[#E5F8F8] text-[#0DB5B5]",
      "Approval Pending": "bg-[#FFF4E5] text-[#FF9500]",
      Draft: "bg-[#F0F0F0] text-[#646464]",
      "In-Active": "bg-[#F0F0F0] text-[#8E8E93]",
      Archived: "bg-[#F0F0F0] text-[#8E8E93]",
      Rejected: "bg-[#FFF0EF] text-[#FF3B30]",
    })[s] ?? "bg-gray-100 text-gray-600";

  const getTimeInMinutes = (m: MealTiming) => {
    let h = parseInt(m.time.hour, 10);
    const min = parseInt(m.time.minute, 10);
    if (m.time.period === "PM" && h !== 12) h += 12;
    if (m.time.period === "AM" && h === 12) h = 0;
    return h * 60 + min;
  };
  const sortedMealTimings = [...mealTimings].sort(
    (a, b) => getTimeInMinutes(a) - getTimeInMinutes(b),
  );

  const LoadingGrid = (height: string) => (
    <div
      className={`flex gap-4 ${height} overflow-x-auto custom-scrollbar-overlay pb-2`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="bg-gray-200 rounded-md shimmer h-full flex-shrink-0 w-[280px]"
        />
      ))}
    </div>
  );

  const EmptyGrid = (height: string) => (
    <div
      className={`flex items-center justify-center bg-white border-[0.5px] border-[#F3F4F6] rounded-md ${height}`}
    >
      <p className="text-[#828282] text-sm 3xl:text-base">
        No meals scheduled yet
      </p>
    </div>
  );

  const MealGrid = (
    <>
      <div
        ref={scrollContainerRef}
        className="flex gap-4 h-full overflow-x-auto custom-scrollbar-overlay pb-2"
      >
        {sortedMealTimings.map((meal) => (
          <div key={meal.id} className="flex-shrink-0 w-[280px]">
            <MealTimingCard
              meal={meal}
              isExpanded={isUserDetailExpanded}
              isViewOnly={isViewOnly ?? false}
              onCreateClick={() =>
                handleCreateClick(meal.mealType, meal.id, meal.fk_user_meal)
              }
              mealOptions={mealOptions}
              onOptionClick={(o) => {
                setSelectedViewOption(o);
                setIsViewModalOpen(true);
              }}
              onOptionEdit={handleEditOption}
              onOptionDuplicate={handleDuplicateOption}
              duplicatingProgress={duplicatingProgress}
              getCardProps={!isViewOnly ? getCardProps : undefined}
              getItemClassName={!isViewOnly ? getItemClassName : undefined}
              getMealTimingDropZoneProps={
                !isViewOnly ? getMealTimingDropZoneProps : undefined
              }
              shouldShowPlaceholder={
                !isViewOnly ? shouldShowPlaceholder : undefined
              }
              onDropAtPosition={!isViewOnly ? handleDropAtPosition : undefined} // ADD THIS
            />
          </div>
        ))}
        {!isViewOnly && (
          <div className="flex-shrink-0 flex items-start px-1">
            <button
              onClick={() => setIsManageMealTimingsOpen(true)}
              className="w-12 h-10 bg-white  border-[0.5px] border-[#E5E5EA] rounded-md flex items-center justify-center shadow-sm"
              title="Add/Edit Meal Timing"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5V19M5 12H19"
                  stroke="#0DB5B5"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
      <ScrollPositionIndicator
        scrollContainerRef={scrollContainerRef}
        totalItems={sortedMealTimings.length}
      />
    </>
  );

  const canEditPlanName =
    (isSuggestionMode && !isRejectedSuggestionMode) ||
    (!isApprovalMode && mealPlan?.status === "Draft");

  const handleStartEditPlanName = () => {
    if (!canEditPlanName || !mealPlan) return;
    setEditingPlanNameValue(mealPlan.plan_name);
    setIsEditingPlanName(true);
    setTimeout(() => planNameInputRef.current?.select(), 0);
  };

  const handleSavePlanName = async () => {
    const trimmed = editingPlanNameValue.trim();
    if (!trimmed || !mealPlan || trimmed === mealPlan.plan_name) {
      setIsEditingPlanName(false);
      return;
    }
    setIsSavingPlanName(true);
    try {
      let data: any;
      if (isAdhocMealMode && planId) {
        data = await renameAdhocMealPlan(planId, { plan_name: trimmed });
      } else {
        const templateId = isSuggestionMode ? (generatedTemplateId ?? mealPlan.id) : mealPlan.id;
        data = await updateTemplateName(templateId, { template_name: trimmed });
      }
      if (data.success === true || data.success === "1" || data.success === 1) {
        setMealPlan((prev) => (prev ? { ...prev, plan_name: trimmed } : prev));
        toast.success(
          "Renamed",
          data.data?.message ||
            (isAdhocMealMode ? "Plan name updated successfully." : "Template name updated successfully."),
        );
      } else {
        toast.error(
          "Failed",
          data.message ||
            (isAdhocMealMode ? "Could not update plan name." : "Could not update template name."),
        );
      }
    } catch {
      toast.error(
        "Error",
        isAdhocMealMode ? "Failed to update plan name." : "Failed to update template name.",
      );
    } finally {
      setIsSavingPlanName(false);
      setIsEditingPlanName(false);
    }
  };

  const handlePlanNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") { e.preventDefault(); handleSavePlanName(); }
    if (e.key === "Escape") { setIsEditingPlanName(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{shimmerStyles}</style>
      <ToastContainer toasts={toast.toasts} onClose={toast.removeToast} />
      <div className="h-screen flex flex-col bg-[#F7F7FA]">
        {/* Header */}
        <div className="flex-shrink-0">
          <PageHeader
            title={
              loading ? (
                "Loading..."
              ) : apiError ? (
                "Error Loading Meal Plan"
              ) : mealPlan ? (
                <div className="flex items-center gap-3">
                  {isEditingPlanName ? (
                    <input
                      ref={planNameInputRef}
                      type="text"
                      value={editingPlanNameValue}
                      onChange={(e) => setEditingPlanNameValue(e.target.value)}
                      onBlur={handleSavePlanName}
                      onKeyDown={handlePlanNameKeyDown}
                      disabled={isSavingPlanName}
                      className="bg-transparent border-b-2 border-[#0DB5B5] outline-none font-semibold text-[#1F1F1F] disabled:opacity-60"
                      style={{ minWidth: "4ch", width: `${editingPlanNameValue.length + 1}ch` }}
                    />
                  ) : (
                    <span
                      onClick={handleStartEditPlanName}
                      className={canEditPlanName ? "cursor-text underline decoration-dashed decoration-transparent hover:decoration-[#0DB5B5] underline-offset-2 transition-colors" : ""}
                      title={canEditPlanName ? "Click to edit" : undefined}
                    >
                      {mealPlan.plan_name}
                    </span>
                  )}
                  <span className="text-[14px] 3xl:text-[16px] font-medium font-inter text-[#646464]">
                    {mealPlan.meal_type}
                  </span>
                  <span className="h-4 w-px bg-[#D1D1D6]" />
                  <span
                    className={`px-2 py-0.5 rounded-[24px] text-xs font-medium font-inter ${getStatusColor(isRejectedSuggestionMode ? "Rejected" : mealPlan.status)}`}
                  >
                    {isRejectedSuggestionMode ? "Rejected" : mealPlan.status}
                  </span>
                </div>
              ) : (
                "Meal Plan"
              )
            }
            breadcrumb={
              isSuggestionMode
                ? `Global template > ${isRejectedSuggestionMode ? "Rejected template" : "Suggested template"} > ${loading ? "..." : mealPlan?.plan_name || "Plan"}`
                : isGlobalTemplateMode
                ? `Global template > ${loading ? "..." : mealPlan?.plan_name || "Meal Plan"}`
                : isAdhocMealMode
                ? `Adhoc meal plan > ${loading ? "..." : mealPlan?.plan_name || "Meal Plan"}`
                : `Clients > Log details > Plans > ${loading ? "..." : mealPlan?.plan_name || "Meal Plan"}`
            }
            breadcrumbLinks={
              isSuggestionMode
                ? ["/global-template", "/global-template", ""]
                : isGlobalTemplateMode
                ? ["/global-template", ""]
                : isAdhocMealMode
                ? ["/adhoc-meal", ""]
                : ["/client", `/client/${userId}`, `/client/${userId}/plans`, ""]
            }
            rightContent={
              isApprovalMode && !isRejectedSuggestionMode ? (
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleReject}
                    disabled={approvalAction === "approve"}
                    className="px-4 py-2 text-[#FF3B30] rounded-md text-sm font-medium hover:bg-[#FFF5F5] transition-colors disabled:opacity-50"
                    style={{ border: "0.5px solid #FF3B30" }}
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleApprove}
                    disabled={approvalAction === "approve"}
                    className="px-4 py-2 text-[#20BCBB] rounded-md text-sm font-medium hover:bg-[#F0FAFA] transition-colors disabled:opacity-50"
                    style={{ border: "0.5px solid #20BCBB" }}
                  >
                    Approve
                  </button>
                </div>
              ) : isGlobalTemplateMode && approvalState?.templateStatus?.toLowerCase() !== "active" ? (
                <div className="flex items-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={handleSaveAsDraft}
                  >
                    Save as draft
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSaveAsTemplate}
                  >
                    Save & publish
                  </Button>
                </div>
              ) : isAdhocMealMode && !loading && !apiError ? (
                mealPlan?.status === "Draft" ? (
                  <Button
                    variant="primary"
                    onClick={handlePublishAdhoc}
                    disabled={isPublishingAdhoc}
                  >
                    {isPublishingAdhoc ? "Publishing..." : "Publish"}
                  </Button>
                ) : (
                  <button
                    onClick={handleDownloadAdhocPdf}
                    disabled={isDownloadingAdhocPdf}
                    className="px-6 py-2 bg-white rounded-md text-sm 3xl:text-base font-medium text-[#0DB5B5] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ border: "1px solid #0DB5B5" }}
                  >
                    {isDownloadingAdhocPdf ? "Downloading..." : "Download PDF"}
                  </button>
                )
              ) : !isSuggestionMode && !loading && !apiError && !isDeactivated && !isViewOnly && mealPlan?.status === "Draft" ? (
                <button
                  onClick={() => navigate(`/client/${userId}/plans`)}
                  className="px-4 py-2 bg-white rounded-md text-sm font-medium text-[#1F1F1F] transition-colors"
                  style={{ border: "0.5px solid #20BCBB" }}
                >
                  Save to draft
                </button>
              ) : undefined
            }
            showSendForApproval={
              !isApprovalMode &&
              !isGlobalTemplateMode &&
              !isAdhocMealMode &&
              !isSuggestionMode &&
              !loading &&
              !apiError &&
              !isDeactivated &&
              !isViewOnly &&
              mealPlan?.status === "Draft"
            }
            onSendForApproval={handleSendForApproval}
          />
        </div>

        {/* Body */}
        {apiError ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-sm text-red-500">{apiError}</p>
              <Button
                variant="primary"
                onClick={() =>
                  isAdhocMealMode
                    ? navigate("/adhoc-meal")
                    : isGlobalTemplateMode
                      ? navigate("/global-template")
                      : navigate(`/client/${userId}/plans`)
                }
                className="mt-4"
              >
                {isAdhocMealMode
                  ? "Back to Adhoc Plans"
                  : isGlobalTemplateMode
                    ? "Back to Templates"
                    : "Back to Plans"}
              </Button>
            </div>
          </div>
        ) : isUserDetailExpanded ? (
          // When expanded: Enable scrolling with proper spacing
          <div className="flex-1 overflow-y-auto custom-scrollbar-overlay scrollbar-visible ">
            <div className="pb-4 min-h-full">
              {isApprovalMode ? (
                submitterInfo && (
                  <div className="mx-6 mb-4">
                    <div className="bg-white px-4 py-3 border-[0.5px] border-[#F3F4F6] rounded-md flex items-center gap-3">
                      {(() => {
                        const photo = apiSubmitterPhoto || submitterInfo.photo || "";
                        return photo ? (
                          <img
                            src={`${BASE_URL}${photo}`}
                            alt={submitterInfo.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                            onError={(e) => {
                              const t = e.currentTarget;
                              t.style.display = "none";
                              const fb = t.nextElementSibling as HTMLElement | null;
                              if (fb) fb.style.display = "flex";
                            }}
                          />
                        ) : null;
                      })()}
                      <div
                        className="w-10 h-10 rounded-full items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                        style={{ backgroundColor: submitterInfo.avatarColor, display: (apiSubmitterPhoto || submitterInfo.photo) ? "none" : "flex" }}
                      >{submitterInfo.avatarInitial}</div>
                      <div>
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-sm text-[#1F1F1F]">{submitterInfo.name}</span>
                          {submitterInfo.goal && <><span className="text-[#B0B0B0] text-lg leading-none mx-0.5">•</span><span className="text-sm text-[#646464]">{submitterInfo.goal}</span></>}
                        </div>
                        <div className="text-xs text-[#8E8E93]">{submitterInfo.role}</div>
                      </div>
                    </div>
                    {generatedTemplateCode && (
                      <div className="mt-3 bg-[#E6FBFB] rounded-md px-4 py-3" style={{ border: "0.5px solid #0DB5B5" }}>
                        <p className="text-xs text-[#8E8E93]">Generated as</p>
                        <p className="text-sm font-semibold text-[#0DB5B5]">{generatedTemplateCode}</p>
                      </div>
                    )}
                    {isRejectedSuggestionMode && (apiRejectionMeta?.reason || rejectionInfo?.reason) && (
                      <div className="mt-3 bg-[#FFF5F5] rounded-md px-4 py-3" style={{ border: "0.5px solid #FF3B30" }}>
                        <p className="text-sm font-semibold text-[#1F1F1F] mb-1">Reason:</p>
                        <p className="text-sm text-[#3D3D3D] mb-2">{apiRejectionMeta?.reason || rejectionInfo?.reason}</p>
                        <div className="flex items-center gap-1 flex-wrap text-xs text-[#8E8E93]">
                          <span>Rejected by {apiRejectionMeta?.reviewedByName || rejectionInfo?.reviewedByName}</span>
                          {(apiRejectionMeta?.reviewedByRole || submitterInfo?.role) && <><span className="text-[#B0B0B0] text-base leading-none mx-0.5">•</span><span>{apiRejectionMeta?.reviewedByRole || submitterInfo?.role}</span></>}
                          {rejectionInfo?.reviewedOn && <><span className="text-[#B0B0B0] text-base leading-none mx-0.5">•</span><span>{new Date(rejectionInfo.reviewedOn).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span></>}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : isGlobalTemplateMode || isAdhocMealMode ? null : (
                <UserDetailSection userId={userIdNumber} onExpandChange={setIsUserDetailExpanded} />
              )}

              {/* Nutrition Stats */}
              {nutritionData && (
                <NutritionStatsDisplay
                  nutritionData={nutritionData}
                  loading={loading}
                  onEditClick={
                    canEditNutrientValues
                      ? () => setIsEditNutrientModalOpen(true)
                      : undefined
                  }
                />
              )}

              {/* Meal Grid - Fixed height when expanded */}
              <div className="px-6 pb-4">
                <div className="h-[calc(100vh-500px)] min-h-[500px] relative">
                  {loading
                    ? LoadingGrid("h-full")
                    : mealTimings.length === 0
                      ? EmptyGrid("h-full")
                      : MealGrid}
                </div>
              </div>
            </div>
          </div>
        ) : (
          // When collapsed: flex container with full height
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-shrink-0">
              {isApprovalMode ? (
                submitterInfo && (
                  <div className="mx-6 mb-4">
                    <div className="bg-white px-4 py-3 border-[0.5px] border-[#F3F4F6] rounded-md flex items-center gap-3">
                      {(() => {
                        const photo = apiSubmitterPhoto || submitterInfo.photo || "";
                        return photo ? (
                          <img
                            src={`${BASE_URL}${photo}`}
                            alt={submitterInfo.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                            onError={(e) => {
                              const t = e.currentTarget;
                              t.style.display = "none";
                              const fb = t.nextElementSibling as HTMLElement | null;
                              if (fb) fb.style.display = "flex";
                            }}
                          />
                        ) : null;
                      })()}
                      <div
                        className="w-10 h-10 rounded-full items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                        style={{ backgroundColor: submitterInfo.avatarColor, display: (apiSubmitterPhoto || submitterInfo.photo) ? "none" : "flex" }}
                      >{submitterInfo.avatarInitial}</div>
                      <div>
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-sm text-[#1F1F1F]">{submitterInfo.name}</span>
                          {submitterInfo.goal && <><span className="text-[#B0B0B0] text-lg leading-none mx-0.5">•</span><span className="text-sm text-[#646464]">{submitterInfo.goal}</span></>}
                        </div>
                        <div className="text-xs text-[#8E8E93]">{submitterInfo.role}</div>
                      </div>
                    </div>
                    {generatedTemplateCode && (
                      <div className="mt-3 bg-[#E6FBFB] rounded-md px-4 py-3" style={{ border: "0.5px solid #0DB5B5" }}>
                        <p className="text-xs text-[#8E8E93]">Generated as</p>
                        <p className="text-sm font-semibold text-[#0DB5B5]">{generatedTemplateCode}</p>
                      </div>
                    )}
                    {isRejectedSuggestionMode && (apiRejectionMeta?.reason || rejectionInfo?.reason) && (
                      <div className="mt-3 bg-[#FFF5F5] rounded-md px-4 py-3" style={{ border: "0.5px solid #FF3B30" }}>
                        <p className="text-sm font-semibold text-[#1F1F1F] mb-1">Reason:</p>
                        <p className="text-sm text-[#3D3D3D] mb-2">{apiRejectionMeta?.reason || rejectionInfo?.reason}</p>
                        <div className="flex items-center gap-1 flex-wrap text-xs text-[#8E8E93]">
                          <span>Rejected by {apiRejectionMeta?.reviewedByName || rejectionInfo?.reviewedByName}</span>
                          {(apiRejectionMeta?.reviewedByRole || submitterInfo?.role) && <><span className="text-[#B0B0B0] text-base leading-none mx-0.5">•</span><span>{apiRejectionMeta?.reviewedByRole || submitterInfo?.role}</span></>}
                          {rejectionInfo?.reviewedOn && <><span className="text-[#B0B0B0] text-base leading-none mx-0.5">•</span><span>{new Date(rejectionInfo.reviewedOn).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span></>}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : isGlobalTemplateMode || isAdhocMealMode ? null : (
                <UserDetailSection userId={userIdNumber} onExpandChange={setIsUserDetailExpanded} />
              )}
            </div>

            {nutritionData && (
              <div className="flex-shrink-0">
                <NutritionStatsDisplay
                  nutritionData={nutritionData}
                  loading={loading}
                  onEditClick={
                    canEditNutrientValues
                      ? () => setIsEditNutrientModalOpen(true)
                      : undefined
                  }
                />
              </div>
            )}

            <div className="flex-1 px-6 pb-4 min-h-0 relative">
              {loading
                ? LoadingGrid("h-full")
                : mealTimings.length === 0
                  ? EmptyGrid("h-full")
                  : MealGrid}
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}

      <CreateDishModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setIsEditMode(false);
          setEditingOptionData(undefined);
        }}
        mealType={selectedMealType}
        userMealId={selectedUserMealId}
        userId={userId ?? "0"}
        optionNumber={getNextOptionNumber(selectedMealTimingId)}
        onCreateOption={handleCreateMealOption}
        editMode={isEditMode}
        editingOptionData={editingOptionData}
        onUpdateOption={handleUpdateMealOption}
        isGlobalTemplateMode={isGlobalTemplateMode}
        templateId={planId}
        isAdhocMealMode={isAdhocMealMode}
        adhocPlanId={planId}
        onShowToast={(type, title, msg) => {
          if (type === "success") toast.success(title, msg);
          else if (type === "error") toast.error(title, msg);
          else if (type === "warning") toast.warning(title, msg);
        }}
      />

      {selectedViewOption && (
        <ViewMealOptionModal
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedViewOption(null);
          }}
          onEdit={
            isViewOnly ? undefined : () => handleEditOption(selectedViewOption)
          }
          onDelete={isViewOnly ? undefined : handleDeleteMealOption}
          onDuplicate={
            isViewOnly
              ? undefined
              : () => {
                  setIsViewModalOpen(false);
                  setSelectedViewOption(null);
                  handleDuplicateOption(selectedViewOption);
                }
          }
          optionName={selectedViewOption.name}
          mealType={
            mealTimings.find((m) => m.id === selectedViewOption.mealTimingId)
              ?.mealType || ""
          }
          dishes={selectedViewOption.dishes}
          instructions={selectedViewOption.instructions}
          totals={selectedViewOption.totals}
          isDeleting={isDeletingOption}
          isViewOnly={isViewOnly}
          userId={userId ?? "0"}
        />
      )}

      <DuplicateMealOptionModal
        isOpen={isDuplicateModalOpen}
        onClose={() => {
          setIsDuplicateModalOpen(false);
          setDuplicatingOption(null);
        }}
        onDuplicate={handleConfirmDuplicate}
        originalName={duplicatingOption?.name || ""}
        isLoading={isDuplicatingOption}
      />

      <MealPlanPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        onSendToClient={handleConfirmSendToClient}
        mealTimings={mealTimings}
        mealOptions={mealOptions}
        nutritionData={nutritionData}
        userId={userId ?? "0"}
        mealPlanId={planId}
        mealPlanType={mealPlan?.meal_type}
        playerId={clientPlayerId}
      />

      {/* Reject Template Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold text-[#1F1F1F] mb-5">Reject template</h2>
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1F1F1F] mb-2">
                What's missing or needs improvement?
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Provide specific feedback or suggestions for changes."
                rows={5}
                className="w-full px-3 py-2.5 border border-[#E0E0E0] rounded-lg text-sm text-[#1F1F1F] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#0DB5B5] resize-none"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => { setIsRejectModalOpen(false); setRejectReason(""); }}
                disabled={isSubmittingReject}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="md"
                onClick={handleSubmitReject}
                disabled={!rejectReason.trim() || isSubmittingReject}
              >
                {isSubmittingReject ? "Rejecting..." : "Reject"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Confirmation Modal */}
      {isApproveConfirmOpen && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-semibold text-[#1F1F1F] mb-3">Are you sure?</h2>
            <p className="text-sm text-[#646464] mb-6 leading-relaxed">
              Once approved, it will be available for nutritionists to use and assign to clients.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => setIsApproveConfirmOpen(false)}
                disabled={isSubmittingApprove}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleConfirmApprove}
                disabled={isSubmittingApprove}
              >
                {isSubmittingApprove ? "Approving..." : "Approve"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit nutrient values — draft adhoc plans only */}
      {canEditNutrientValues && nutritionData && (
        <EditNutrientValuesModal
          isOpen={isEditNutrientModalOpen}
          onClose={() => setIsEditNutrientModalOpen(false)}
          onSave={handleSaveNutrientValues}
          isSaving={isSavingNutrients}
          initialValues={{
            totalCaloriesMin: String(nutritionData.total_cals_min ?? ""),
            totalCaloriesMax: String(nutritionData.total_cals_max ?? ""),
            totalProteinMin: String(nutritionData.protein_min ?? ""),
            totalProteinMax: String(nutritionData.protein_max ?? ""),
            totalCarbsMin: String(nutritionData.carbs_min ?? ""),
            totalCarbsMax: String(nutritionData.carbs_max ?? ""),
            totalFatsMin: String(nutritionData.fat_min ?? ""),
            totalFatsMax: String(nutritionData.fat_max ?? ""),
            totalGLMin: String(nutritionData.GL_min ?? ""),
            totalGLMax: String(nutritionData.GL_max ?? ""),
          }}
        />
      )}

      {/* Manage timings — not available when Active */}
      {!isViewOnly && (
        <MealPlanDuplicateAndTimingsPopup
          isOpen={isManageMealTimingsOpen}
          onClose={() => setIsManageMealTimingsOpen(false)}
          onSubmit={handleUpdateMealTimings}
          userId={userIdNumber}
          isEditMode={true}
          existingMealTimings={mealTimings}
          mealPlanId={planId || ""}
          isGlobalTemplateMode={isGlobalTemplateMode}
          isAdhocMealMode={isAdhocMealMode}
          onShowToast={(type, title, msg) => {
            if (type === "success") toast.success(title, msg);
            else if (type === "error") toast.error(title, msg);
            else if (type === "warning") toast.warning(title, msg);
          }}
        />
      )}
    </>
  );
};

export default MealPlanDetailsPage;